import { UiButtonDirective } from '@template-ui/directives';
import { requireWorkflowPayloadVersion } from './platform-workflow-definition-lifecycle.models';
import { CommonModule } from '@angular/common';
import {
  Component,
  DestroyRef,
  ElementRef,
  EventEmitter,
  Input,
  HostListener,
  OnChanges,
  Output,
  SimpleChanges,
  ViewChild,
  inject,
  signal,
} from '@angular/core';
import { Validators } from '@angular/forms';
import { EzFormComponent } from '@template-ui/components/ez-form/ez-form.component';
import { UiLibraryModule } from '@template-ui/template-ui.module';
import { ButtonConfig, InputSelectOption } from '@template-ui/types';
import { EzFormInputType, InputConfig } from '@template-ui/types/ez-form.types';

import {
  WorkflowAllowedTransition,
  WorkflowDecisionRequirement,
  WorkflowDefinitionDocument,
  WorkflowDefinitionAuthoringReferences,
  WorkflowDefinitionNode,
  WorkflowNotificationRule,
} from './platform-workflow-definition-lifecycle.models';
import { WorkflowRegistryDiagnosticDisplayError } from './platform-workflow-registry-diagnostics.models';
import { WorkflowDraftConfirmationService } from './workflow-draft-confirmation.service';
import { WorkflowNotificationRuleEditorComponent } from './workflow-notification-rule-editor.component';
import { WorkflowStatusEditorComponent, type WorkflowStatusOwnedDocumentChange } from './workflow-status-editor.component';
import { WorkflowStepProgressEditorComponent, type WorkflowStepProgressOwnedDocumentChange } from './workflow-step-progress-editor.component';
import { applyWorkflowStepProgress, type WorkflowStepProgressDraft } from './workflow-step-progress-authoring';
import { emptyStatusConfiguration, replaceStatusConfiguration } from './workflow-status-authoring';
import { workflowEditorPendingOwnerScopes, type WorkflowEditorPendingScope, type WorkflowEditorBufferState } from './workflow-editor-pending-state';
import { WorkflowOwnerViewState } from './workflow-owner-view-state';
import type { WorkflowStatusOwnerRequest } from './workflow-status-owner-request';
import type { WorkflowStepBypassDataIntent, WorkflowStepBypassDataOwnerOrigin, WorkflowStepBypassDataRequest } from './workflow-step-bypass-data-request';

type TransitionScopeCode = 'workflow' | 'step';
const readableArray = <T>(value: T[] | null | undefined): T[] => Array.isArray(value) ? value : [];
const savedObject = (value: unknown): value is Record<string, unknown> => !!value && typeof value === 'object' && !Array.isArray(value);

interface TransitionFormValues {
  scopeCode: TransitionScopeCode;
  stepNodeId: string;
  fromStateCode: string;
  toStateCode: string;
}

interface TransitionSelection {
  scopeCode: TransitionScopeCode;
  stepNodeId: string | null;
  index: number;
}

interface TransitionScopeSelection {
  scopeCode: TransitionScopeCode;
  stepNodeId: string | null;
}

export interface WorkflowDraftTransitionRow extends TransitionSelection {
  key: string;
  scopeLabel: string;
  stepLabel: string;
  transition: WorkflowAllowedTransition | null;
}

export interface WorkflowDraftTransitionScope extends TransitionScopeSelection {
  key: string;
  label: string;
  detail: string;
  isAuthorable: boolean;
  transitionCount: number;
  decisionRuleCount: number;
}

export interface WorkflowDraftTransitionDecisionRulesRequest {
  scopeCode: TransitionScopeCode;
  stepNodeId: string | null;
  transitionIndex: number;
}

interface LifecycleImpactPreview {
  operation: 'add' | 'remove';
  nodeId: string;
  workflowCode: string;
  definitionVersionCode: string;
  resetVersion: number;
  contextNodeId: string | null | undefined;
  nodeSnapshot: string;
  selectionKey: string | null;
  before: string;
  after: string;
  attachedDecisionCount: number;
  attachedNotificationCount: number;
  attachedMessageCount: number;
  transitionIndex: number | null;
}
type StepStatusesView = 'overview' | 'step-labels' | 'bypass';
interface StepStatusesNavigation { owner: string; source: string; context: string | null; view: StepStatusesView; }
interface StepStatusesSettlement { kind: 'progress' | 'labels'; owner: string; source: string; nodeId: string; accepted: boolean; rejected: boolean; }

@Component({
  selector: 'phms-platform-workflow-draft-transition-editor',
  imports: [
    UiButtonDirective,
    CommonModule,
    UiLibraryModule,
    WorkflowStatusEditorComponent,
    WorkflowStepProgressEditorComponent,
  ],
  templateUrl: './platform-workflow-draft-transition-editor.component.html',
  styleUrl: './platform-workflow-draft-transition-editor.component.scss',
})
export class PlatformWorkflowDraftTransitionEditorComponent implements OnChanges {
  @Input() contextual = false;
  @Input() inspectionMode = false;
  @Input() document: WorkflowDefinitionDocument | null = null;
  @Input() externalWorkflowStatuses = false;
  @Input() stepStatusesWorkspace = false;
  @Input() requireProgressOwnerAcknowledgement = false;
  @Input() latestDocument: (() => WorkflowDefinitionDocument | null) | null = null;
  @Input() ownerContext: (() => unknown) | null = null;
  @Input() viewContext: (() => unknown) | null = null;
  @Input() workflowStatusesView: (() => string | null) | null = null;
  @Input() workflowStatusesViewState: string | null | undefined = undefined;
  private authoringAllowed = false;
  @Input() set canAuthor(value: boolean) { this.authoringAllowed = value; }
  get canAuthor(): boolean { return this.authoringAllowed && !this.hasOpaqueTransitionCollections; }
  get hasOpaqueTransitionCollections(): boolean {
    const document = this.document;
    if (!document) return false;
    const opaque = (value: unknown) => value != null && !Array.isArray(value);
    const transitionsOpaque = (value: unknown) => opaque(value) || Array.isArray(value) && value.some(transition => transition != null
      && (!savedObject(transition) || opaque(transition['requiredDecisions']) || opaque(transition['requiredDecisionKinds']) || opaque(transition['notifications'])));
    return !Array.isArray(document.nodes) || document.nodes.some(node => !savedObject(node) || typeof node.id !== 'string'
      || opaque(node.requiredDecisions) || transitionsOpaque(node.allowedTransitions))
      || opaque(document.requiredDecisions) || transitionsOpaque(document.workflowTransitions);
  }
  readonly opaqueCollectionMessage = 'Some saved transition or decision-check collections are unavailable. Counts cover readable entries only; authoring is read-only and the original content is preserved.';
  @Input() interactionBlocked = false;
  @Input() resetContextStepNodeId: string | null | undefined;
  @Input() resetVersion = 0;
  @Input() stateCodes: string[] = [];
  @Input() roleCodes: string[] = [];
  @Input() queueCodes: string[] = [];
  @Input() managerNotificationTargetCodes: string[] = [];
  @Input() externalNotificationTargetCodes: string[] = [];
  @Input() referenceLoading = false;
  @Input() routingContracts: NonNullable<
    WorkflowDefinitionAuthoringReferences['routingContracts']
  > = [];
  @Input() referenceError: WorkflowRegistryDiagnosticDisplayError | null = null;

  @Output() readonly documentChange =
    new EventEmitter<WorkflowDefinitionDocument>();
  @Output() readonly pendingChangesChange = new EventEmitter<boolean>();
  @Output() readonly retryReferencesRequested = new EventEmitter<void>();
  @Output() readonly decisionRulesRequested =
    new EventEmitter<WorkflowDraftTransitionDecisionRulesRequest>();
  @Output() readonly notificationContextRequested = new EventEmitter<{
    nodeId: string;
    path: string;
  }>();
  @Output() readonly stepDecisionRulesRequested = new EventEmitter<string>();
  @Output() readonly completionRequirementsRequested =
    new EventEmitter<string>();
  @Output() readonly workflowStatusesRequested = new EventEmitter<string>();
  @Output() readonly workflowStatusesOwnerRequested = new EventEmitter<WorkflowStatusOwnerRequest>();
  @Output() readonly progressOwnedDocumentChange = new EventEmitter<WorkflowStepProgressOwnedDocumentChange>();
  @Output() readonly bypassDataOwnerRequested = new EventEmitter<WorkflowStepBypassDataRequest>();
  private emittingProgressChange: WorkflowStepProgressOwnedDocumentChange | null = null;
  private emittingBypassDataRequest: WorkflowStepBypassDataRequest | null = null;
  get currentlyEmittingProgressChange(): WorkflowStepProgressOwnedDocumentChange | null { return this.emittingProgressChange; }
  get currentlyEmittingBypassDataRequest(): WorkflowStepBypassDataRequest | null { return this.emittingBypassDataRequest; }
  get pendingStepProgressDraft(): WorkflowStepProgressDraft | null {
    return this.progressEditor?.hasPendingChanges() ? this.progressEditor.draftSnapshot : null;
  }
  get isSettlingStepProgress(): boolean { return this.stepStatusesSettlement?.kind === 'progress'; }
  private externalStatusNavigationOwner: string | null = null;
  private externalStatusNavigationView: string | null = null;
  readonly stepStatusesView = signal<StepStatusesView>('overview');
  private stepStatusesNamespace = '';
  private stepStatusesGeneration = 0;
  private stepStatusesFocus = 0;
  private stepStatusesSettlement: StepStatusesSettlement | null = null;
  private stepStatusesSavedSources = '';
  readonly stepStatusesMessage = signal<string | null>(null);
  readonly currentStepStatusesDocument = () => this.latestDocument ? this.latestDocument() : this.document;
  readonly progressOwnerActive = () => this.stepStatusesView() !== 'step-labels' && this.stepStatusesParentCurrent();
  readonly labelsOwnerActive = () => this.stepStatusesView() === 'step-labels' && this.stepStatusesParentCurrent();
  readonly progressActiveView = () => this.stepStatusesView() === 'step-labels' ? null : this.stepStatusesChildView(this.stepStatusesView());
  readonly labelsActiveView = () => this.stepStatusesChildView('step-labels');
  get stepLabelsSummary(): string {
    const configuration = this.activeHumanStep?.statusConfiguration;
    return configuration == null ? 'No optional step labels are configured.'
      : !savedObject(configuration) || !Array.isArray(configuration.statuses) || !Array.isArray(configuration.rules)
        ? 'Saved step labels are unavailable for editing. Their original settings are preserved.'
        : `${configuration.statuses.length} configured step labels.`;
  }
  get stepStatusesNavigation(): StepStatusesNavigation { return { owner: JSON.stringify(this.ownerContext?.()), source: JSON.stringify(this.currentStepStatusesDocument()),
    context: this.workflowStatusesRequestContext(), view: this.stepStatusesView() }; }
  workflowStatusesRequestContext(): string | null {
    return this.stepStatusesWorkspace && this.activeHumanStep
      ? JSON.stringify([this.stepStatusesNamespace, this.activeHumanStep.id, this.stepStatusesView(), this.stepStatusesGeneration]) : null;
  }

  /** This live baseline reader remains useful while Data hides the retained owner. */
  readBypassDataOrigin(): WorkflowStepBypassDataOwnerOrigin | null {
    if (!this.canAuthor || this.inspectionMode || !this.stepStatusesScopeCurrent() || this.stepStatusesView() !== 'bypass') return null;
    const origin = this.progressEditor?.readBypassDataOrigin(), localContext = this.workflowStatusesRequestContext();
    const document = this.currentStepStatusesDocument();
    if (!origin || !localContext || !document || origin.nodeId !== this.activeHumanStep?.id) return null;
    return { ...origin, draft: structuredClone(origin.draft), localContext, namespace: this.stepStatusesNamespace,
      owner: JSON.stringify(this.ownerContext?.()), source: JSON.stringify(document) };
  }

  onBypassDataRequested(intent: WorkflowStepBypassDataIntent): void {
    const origin = this.readBypassDataOrigin(), view = this.workflowStatusesView?.();
    if (!intent || !origin || !view || this.emittingBypassDataRequest || !this.stepStatusesParentCurrent()
      || this.formDirty() || this.notificationEditorPending || this.lifecycleImpact() || origin.state === 'stale'
      || intent.nodeId !== origin.nodeId || intent.owner !== origin.owner || intent.source !== origin.source
      || intent.view !== 'bypass' || this.progressEditor?.ownerView !== 'bypass'
      || !intent.originContext || intent.originContext !== this.progressEditor.bypassDataRequestContext()
      || JSON.stringify(intent.draft) !== JSON.stringify(origin.draft)) return;
    const target = intent.target;
    if (!target || (target.kind === 'add-boolean' ? Object.keys(target).length !== 1
      : target.kind !== 'review-field' || Object.keys(target).length !== 2 || typeof target.key !== 'string'
        || !target.key.trim() || target.key !== origin.draft.values.processDataKey)) return;
    const request: WorkflowStepBypassDataRequest = { nodeId: origin.nodeId, owner: origin.owner, source: origin.source,
      view, originContext: intent.originContext, draft: structuredClone(origin.draft), target: structuredClone(target), localContext: origin.localContext };
    this.emittingBypassDataRequest = request;
    try { this.bypassDataOwnerRequested.emit(request); }
    finally { this.emittingBypassDataRequest = null; }
  }

  /** Root restores its destination first; focus waits for both retained owners' fresh echoes. */
  returnToBypassFromData(path = '$.stepProgressConfiguration.bypass.processDataKey'): boolean {
    if (!this.readBypassDataOrigin() || !this.workflowStatusesView?.()) return false;
    this.revealStepStatusesView('bypass');
    this.focusStepStatusOwner('progress', path);
    return true;
  }

  @ViewChild('effectiveLifecycleHeading')
  private effectiveLifecycleHeading?: ElementRef<HTMLElement>;

  @ViewChild('lifecycleImpactHeading')
  private lifecycleImpactHeading?: ElementRef<HTMLElement>;

  @ViewChild('transitionForm')
  private transitionForm?: EzFormComponent;

  @ViewChild('transitionEditorHeading')
  private transitionEditorHeading?: ElementRef<HTMLElement>;

  @ViewChild('transitionSectionHeading')
  private transitionSectionHeading?: ElementRef<HTMLElement>;

  @ViewChild('transitionError')
  private transitionError?: ElementRef<HTMLElement>;

  @ViewChild(WorkflowNotificationRuleEditorComponent)
  private notificationEditor?: WorkflowNotificationRuleEditorComponent;

  @ViewChild(WorkflowStatusEditorComponent)
  private statusEditor?: WorkflowStatusEditorComponent;
  @ViewChild(WorkflowStepProgressEditorComponent)
  private progressEditor?: WorkflowStepProgressEditorComponent;
  readonly businessStatusDirty = signal(false);
  readonly progressSettingsDirty = signal(false);
  private lastPendingState = false;

  readonly selectedTransition = signal<TransitionSelection | null>(null);
  readonly selectedScope = signal<TransitionScopeSelection>({
    scopeCode: 'workflow',
    stepNodeId: null,
  });
  readonly isAddingTransition = signal(false);
  readonly formDirty = signal(false);
  readonly statusMessage = signal<string | null>(null);
  readonly localError = signal<string | null>(null);
  readonly lifecycleImpact = signal<LifecycleImpactPreview | null>(null);
  readonly customizationExpanded = signal(false);

  transitionFormValues: TransitionFormValues = this.emptyFormValues();
  transitionFormConfig: InputConfig[] = [];

  private lastResetVersion: number | null = null;
  private lastResetContextStepNodeId: string | null | undefined;
  private readonly initiallyNullStepCollections = new Set<string>();
  private initiallyNullWorkflowCollection = false;
  private notificationFormDirty = false;
  private pendingFormBaseline: string | null = null;
  private pendingNotificationBaseline: string | null = null;
  private readonly ownerViews = inject(WorkflowOwnerViewState, { optional: true }) ?? new WorkflowOwnerViewState();
  private readonly destroyRef = inject(DestroyRef);
  private readonly hostElement = inject<ElementRef<HTMLElement>>(ElementRef);
  private readonly host = inject<ElementRef<HTMLElement>>(ElementRef);
  private localConfirmationInProgress = false;
  private lastOpaqueTransitionCollections = false;
  private selectedTransitionIdentity: {
    scopeCode: TransitionScopeCode;
    stepNodeId: string | null;
    fromStateCode: string;
    toStateCode: string;
  } | null = null;

  constructor(
    private readonly confirmation: WorkflowDraftConfirmationService,
  ) {}

  ngOnChanges(changes: SimpleChanges): void {
    const workflowStatusesView = this.externalWorkflowStatuses ? this.workflowStatusesView?.() ?? null : null;
    if (!this.externalWorkflowStatuses || this.workflowStatusesViewState === undefined || this.workflowStatusesViewState === workflowStatusesView)
      this.externalStatusNavigationView = workflowStatusesView;
    if (this.externalWorkflowStatuses && (this.externalStatusNavigationOwner === null || changes['resetVersion']
      || !this.formDirty() && !this.businessStatusDirty() && !this.progressSettingsDirty() && !this.notificationEditorPending && !this.lifecycleImpact()))
      this.externalStatusNavigationOwner = JSON.stringify(this.ownerContext?.()) ?? '';
    if (this.stepStatusesWorkspace) {
      const namespace = this.currentStepStatusesNamespace(this.document);
      const sources = this.readStepStatusesSources(this.document);
      if (changes['document'] && namespace === this.stepStatusesNamespace && sources === this.stepStatusesSavedSources)
        this.externalStatusNavigationOwner = JSON.stringify(this.ownerContext?.()) ?? '';
      if (namespace !== this.stepStatusesNamespace) {
        if (this.stepStatusesNamespace) this.ownerViews.remember(this.stepStatusesNamespace, this.stepStatusesView());
        this.stepStatusesNamespace = namespace; this.stepStatusesGeneration++; this.stepStatusesFocus++;
        this.stepStatusesView.set(this.ownerViews.read<StepStatusesView>(namespace) ?? 'overview');
      }
      this.stepStatusesSavedSources = sources;
    }
    const previouslyOpaque = this.lastOpaqueTransitionCollections;
    this.lastOpaqueTransitionCollections = this.hasOpaqueTransitionCollections;
    if (this.lastResetVersion !== this.resetVersion) {
      this.lastResetVersion = this.resetVersion;
      this.lastResetContextStepNodeId = this.resetContextStepNodeId;
      this.captureInitialCollectionShapes();
      this.resetFromDocument();
      if (this.hasOpaqueTransitionCollections) this.statusMessage.set(this.opaqueCollectionMessage);
      return;
    }

    if (!this.document) {
      this.clearEditor();
      return;
    }

    if (changes['document']) {
      this.captureNewNullCollectionShapes();
    }

    if (this.lastResetContextStepNodeId !== this.resetContextStepNodeId) {
      const currentScope = this.selectedScope();
      const contextMatches =
        this.resetContextStepNodeId === null
          ? currentScope.scopeCode === 'workflow'
          : this.resetContextStepNodeId !== undefined &&
            currentScope.scopeCode === 'step' &&
            currentScope.stepNodeId === this.resetContextStepNodeId;
      if (contextMatches) {
        this.lastResetContextStepNodeId = this.resetContextStepNodeId;
      } else {
        if (
          this.formDirty() ||
          this.businessStatusDirty() ||
          this.progressSettingsDirty() ||
          this.notificationEditorPending ||
          this.lifecycleImpact()
        )
          return;
        this.lastResetContextStepNodeId = this.resetContextStepNodeId;
        this.resetFromDocument();
        return;
      }
    }

    if (!this.resolveScope(this.selectedScope())) {
      if (this.formDirty() || this.notificationEditorPending || this.businessStatusDirty() || this.progressSettingsDirty() || this.lifecycleImpact()) {
        this.showLocalError('This saved status-change scope is no longer available. Your input is kept. Reset changes to review current settings.');
        return;
      }
      this.resetFromDocument();
      return;
    }

    const selection = this.selectedTransition();
    if (selection && !this.resolveSelection(selection)) {
      if (this.formDirty() || this.notificationEditorPending) return;
      this.selectFirstTransitionInScope(false);
      return;
    }
    if (selection) {
      const row = this.resolveSelection(selection);
      if (row && row.index !== selection.index)
        this.selectedTransition.set({ ...selection, index: row.index });
    }

    if (changes['document'] && previouslyOpaque !== this.lastOpaqueTransitionCollections && !this.formDirty() && !this.notificationEditorPending
      && !this.businessStatusDirty() && !this.progressSettingsDirty() && !this.lifecycleImpact()) {
      const row = this.selectedRow;
      if (row) this.loadSelection(row);
    }
    if (this.hasOpaqueTransitionCollections) this.statusMessage.set(this.opaqueCollectionMessage);
    if (
      changes['stateCodes'] ||
      changes['canAuthor'] ||
      changes['interactionBlocked']
    ) {
      this.rebuildFormConfig();
    }
  }

  get referenceDataReady(): boolean {
    return this.stateCodes.length > 0;
  }

  get rows(): WorkflowDraftTransitionRow[] {
    const document = this.document;
    if (!document) {
      return [];
    }

    const workflowRows = readableArray(document.workflowTransitions).map(
      (transition, index) => ({
        key: `workflow:${index}`,
        scopeCode: 'workflow' as const,
        stepNodeId: null,
        index,
        scopeLabel: 'Whole workflow',
        stepLabel: 'Whole workflow',
        transition,
      }),
    );
    const stepRows = this.transitionScopeNodes.flatMap((node) =>
      readableArray(node.allowedTransitions).map((transition, index) => ({
        key: `step:${node.id}:${index}`,
        scopeCode: 'step' as const,
        stepNodeId: node.id,
        index,
        scopeLabel: 'Work step',
        stepLabel: this.nodeLabel(node),
        transition,
      })),
    );

    return [...workflowRows, ...stepRows];
  }

  get scopes(): WorkflowDraftTransitionScope[] {
    const workflowScope = this.toScope({
      scopeCode: 'workflow',
      stepNodeId: null,
    });
    return [
      workflowScope,
      ...this.transitionScopeNodes.map((node) =>
        this.toScope({
          scopeCode: 'step',
          stepNodeId: node.id,
        }),
      ),
    ];
  }

  get activeRows(): WorkflowDraftTransitionRow[] {
    const scope = this.selectedScope();
    return this.rows.filter(
      (row) =>
        row.scopeCode === scope.scopeCode &&
        row.stepNodeId === scope.stepNodeId,
    );
  }

  get selectedRow(): WorkflowDraftTransitionRow | null {
    const selection = this.selectedTransition();
    return selection ? this.resolveSelection(selection) : null;
  }

  get activeScopeDefinition(): WorkflowDraftTransitionScope | null {
    const selection = this.selectedScope();
    return (
      this.scopes.find(
        (scope) =>
          scope.scopeCode === selection.scopeCode &&
          scope.stepNodeId === selection.stepNodeId,
      ) ?? null
    );
  }

  get activeHumanStep(): WorkflowDefinitionNode | null {
    const scope = this.selectedScope();
    if (scope.scopeCode !== 'step') return null;
    return (
      this.humanStepNodes.find((node) => node.id === scope.stepNodeId) ?? null
    );
  }

  get usesDefaultLifecycle(): boolean {
    return !!this.activeHumanStep && !this.activeLifecycleUnavailable && this.activeRows.length === 0;
  }

  get activeLifecycleUnavailable(): boolean {
    return this.scopeCollectionOpaque(this.selectedScope());
  }
  scopeCollectionOpaque(scope: TransitionScopeSelection): boolean {
    if (scope.scopeCode === 'step' && this.document && !Array.isArray(this.document.nodes)) return true;
    const value = scope.scopeCode === 'workflow' ? this.document?.workflowTransitions
      : this.readableNodes().find(node => node.id === scope.stepNodeId)?.allowedTransitions;
    return value != null && !Array.isArray(value);
  }
  get completionDecisionCountsUnavailable(): boolean { return this.activeLifecycleUnavailable || this.effectiveDecisionCollectionsOpaque(this.completionRow?.transition ?? null); }

  get completionAllowed(): boolean {
    return (
      this.usesDefaultLifecycle ||
      this.activeRows.some((row) => this.isCompletionTransition(row.transition))
    );
  }

  get hasUnsupportedProgressRules(): boolean {
    return (
      !!this.activeHumanStep?.stepProgressConfiguration &&
      this.activeRows.some(
        (row) => !this.isCompletionTransition(row.transition),
      )
    );
  }

  get completionRow(): WorkflowDraftTransitionRow | null {
    return (
      this.activeRows.find((row) =>
        this.isCompletionTransition(row.transition),
      ) ?? null
    );
  }

  get completionDecisionCount(): number {
    return this.effectiveDecisionCount(this.completionRow?.transition ?? null);
  }

  get selectedIsEffectiveCompletion(): boolean {
    return (
      !!this.selectedRow && this.completionRow?.index === this.selectedRow.index
    );
  }

  get canApplyLifecycleImpact(): boolean {
    const preview = this.lifecycleImpact();
    const step = this.activeHumanStep;
    return (
      !!preview &&
      !!step &&
      !!this.document &&
      this.canAuthor &&
      !this.inspectionMode &&
      !this.interactionBlocked &&
      !this.referenceLoading &&
      !this.notificationEditorPending &&
      preview.workflowCode === this.document.workflowCode &&
      preview.definitionVersionCode === this.document.definitionVersionCode &&
      preview.resetVersion === this.resetVersion &&
      preview.contextNodeId === this.resetContextStepNodeId &&
      preview.nodeId === step.id &&
      preview.nodeSnapshot === JSON.stringify(step) &&
      preview.selectionKey === this.selectedKey &&
      this.readableNodes().filter((node) => node.id === step.id).length === 1 &&
      (preview.operation !== 'add' ||
        (this.referenceDataReady && this.canAddCompletionRule))
    );
  }

  get canAddCompletionRule(): boolean {
    return (
      !!this.activeHumanStep &&
      this.readableNodes().filter(
        (node) => node.id === this.activeHumanStep?.id,
      ).length === 1 &&
      this.stateCodes.includes('in_progress') &&
      this.stateCodes.includes('completed') &&
      !this.activeRows.some((row) =>
        this.isCompletionTransition(row.transition),
      )
    );
  }

  isCompletionTransition(
    transition: WorkflowAllowedTransition | null,
  ): boolean {
    return (
      !!transition &&
      this.transitionIdentity(transition) ===
        this.transitionIdentity({
          fromStateCode: 'in_progress',
          toStateCode: 'completed',
        })
    );
  }

  transitionApplicability(row: WorkflowDraftTransitionRow): string {
    if (!this.isCompletionTransition(row.transition))
      return 'Retained · no staff action';
    return this.completionRow?.index === row.index
      ? 'Used by completion'
      : 'Retained duplicate · first matching rule is used';
  }

  stepDecisionCount(): number {
    return this.transitionDecisionCount(this.stepDecisionRequirements());
  }

  ownerDecisionCount(): number {
    return this.transitionDecisionCount(this.ownerDecisionRequirements());
  }

  effectiveDecisionCount(transition: WorkflowAllowedTransition | null): number {
    const owner = this.ownerDecisionRequirements();
    return this.transitionDecisionCount({
      fromStateCode: 'in_progress',
      toStateCode: 'completed',
      requiredDecisionKinds: [
        ...readableArray(owner.requiredDecisionKinds),
        ...readableArray(transition?.requiredDecisionKinds),
      ],
      requiredDecisions: [
        ...readableArray(owner.requiredDecisions),
        ...readableArray(transition?.requiredDecisions),
      ],
    });
  }

  notificationCount(transition: WorkflowAllowedTransition): number {
    return readableArray(transition.notifications).filter(Boolean).length;
  }

  trackTransitionRow(_index: number, row: WorkflowDraftTransitionRow): string {
    return row.key;
  }

  trackTransitionScope(
    _index: number,
    scope: WorkflowDraftTransitionScope,
  ): string {
    return scope.key;
  }

  decisionCollectionsOpaque(transition: WorkflowAllowedTransition | null): boolean {
    return !!transition && (transition.requiredDecisions != null && !Array.isArray(transition.requiredDecisions)
      || transition.requiredDecisionKinds != null && !Array.isArray(transition.requiredDecisionKinds));
  }
  effectiveDecisionCollectionsOpaque(transition: WorkflowAllowedTransition | null): boolean {
    return this.decisionCollectionsOpaque(transition) || this.decisionCollectionsOpaque(this.ownerDecisionRequirements());
  }
  private readableNodes(document: WorkflowDefinitionDocument | null = this.document): WorkflowDefinitionNode[] {
    return readableArray(document?.nodes).filter(node => savedObject(node) && typeof node.id === 'string');
  }

  get humanStepNodes(): WorkflowDefinitionNode[] {
    return this.readableNodes()
      .filter((node) => node.typeCode === 'human_step')
      .sort(
        (left, right) =>
          left.displayOrder - right.displayOrder ||
          left.id.localeCompare(right.id),
      );
  }

  get transitionScopeNodes(): WorkflowDefinitionNode[] {
    return this.readableNodes()
      .filter(
        (node) =>
          node.typeCode === 'human_step' ||
          (node.allowedTransitions?.length ?? 0) > 0,
      )
      .sort(
        (left, right) =>
          left.displayOrder - right.displayOrder ||
          left.id.localeCompare(right.id),
      );
  }

  get workStepScopes(): WorkflowDraftTransitionScope[] {
    return this.scopes.filter(
      (scope) => scope.scopeCode === 'step' && scope.isAuthorable,
    );
  }

  get evidenceScopes(): WorkflowDraftTransitionScope[] {
    return this.scopes.filter(
      (scope) => scope.scopeCode === 'step' && !scope.isAuthorable,
    );
  }

  get actionButtons(): ButtonConfig[] {
    const blocked =
      this.interactionBlocked ||
      this.referenceLoading ||
      !this.referenceDataReady ||
      !!this.lifecycleImpact();
    const selectedRow = this.selectedRow;
    const hasEditableForm =
      !this.activeHumanStep &&
      (this.isAddingTransition() || !!selectedRow?.transition);
    const isAuthorableScope = this.activeScopeDefinition?.isAuthorable ?? false;
    return [
      {
        id: 'applyTransition',
        buttonText: this.isAddingTransition()
          ? 'Add status change'
          : 'Apply status change',
        disabled:
          !this.canAuthor ||
          (!this.formDirty() && !this.isAddingTransition()) ||
          blocked ||
          !hasEditableForm ||
          !isAuthorableScope,
        hidden: !this.canAuthor || !hasEditableForm || !isAuthorableScope,
      },
      {
        id: 'resetTransition',
        buttonText: this.isAddingTransition() ? 'Cancel' : 'Reset form',
        severity: 'secondary',
        disabled: blocked,
        hidden: !this.canAuthor || !hasEditableForm || !isAuthorableScope,
      },
      {
        id: 'removeTransition',
        buttonText:
          selectedRow?.transition === null
            ? 'Remove invalid entry'
            : 'Remove status change',
        severity: 'secondary',
        disabled:
          blocked ||
          this.notificationEditorPending ||
          this.isAddingTransition() ||
          !this.selectedTransition(),
        hidden: !this.canAuthor || this.isAddingTransition(),
      },
    ];
  }

  get selectedKey(): string | null {
    const selection = this.selectedTransition();
    if (!selection) {
      return null;
    }
    return selection.scopeCode === 'workflow'
      ? `workflow:${selection.index}`
      : `step:${selection.stepNodeId}:${selection.index}`;
  }

  get selectedScopeKey(): string {
    const selection = this.selectedScope();
    return this.scopeKey(selection.scopeCode, selection.stepNodeId);
  }

  get notificationEditorPending(): boolean {
    return this.notificationFormDirty;
  }

  private transitionsFrom(document: WorkflowDefinitionDocument | null, scope = this.selectedScope()): Array<WorkflowAllowedTransition | null> | null {
    if (!document) return null;
    if (scope.scopeCode === 'workflow') return document.workflowTransitions == null ? [] : Array.isArray(document.workflowTransitions) ? document.workflowTransitions : null;
    const nodes = this.readableNodes(document).filter(node => node.id === scope.stepNodeId && node.typeCode === 'human_step');
    return nodes.length === 1 ? nodes[0].allowedTransitions == null ? [] : Array.isArray(nodes[0].allowedTransitions) ? nodes[0].allowedTransitions : null : null;
  }
  private pendingFormSource(document: WorkflowDefinitionDocument | null): string {
    return JSON.stringify([document && [document.workflowCode, document.definitionVersionCode, document.packReference, document.payloadSchemaVersion],
      this.resetVersion, this.selectedScope(), this.selectedTransitionIdentity, this.transitionsFrom(document)]);
  }
  private currentPendingTransition(document: WorkflowDefinitionDocument | null): WorkflowAllowedTransition | null {
    const identity = this.selectedTransitionIdentity;
    if (!identity) return null;
    const matches = this.transitionsFrom(document, identity)?.filter(transition => transition &&
      transition.fromStateCode === identity.fromStateCode && transition.toStateCode === identity.toStateCode) ?? [];
    return matches.length === 1 ? matches[0] : null;
  }
  private pendingNotificationSource(document: WorkflowDefinitionDocument | null): string {
    return JSON.stringify([document && [document.workflowCode, document.definitionVersionCode, document.packReference], this.resetVersion,
      this.selectedTransitionIdentity, this.currentPendingTransition(document)]);
  }
  private pendingFormState(document: WorkflowDefinitionDocument | null): WorkflowEditorBufferState {
    if (!this.formDirty()) return 'clean';
    const transitions = this.transitionsFrom(document);
    if (!document || !transitions || this.pendingFormSource(document) !== this.pendingFormBaseline ||
      (!this.isAddingTransition() && !this.currentPendingTransition(document))) return 'stale';
    if (!this.canAuthor || this.interactionBlocked || this.referenceLoading || !this.referenceDataReady ||
      !this.transitionForm?.theFormGroup || this.transitionForm.theFormGroup.invalid) return 'invalid';
    const from = this.transitionFormValues.fromStateCode.trim(), to = this.transitionFormValues.toStateCode.trim();
    if (!this.stateCodes.includes(from) || !this.stateCodes.includes(to)) return 'invalid';
    const current = this.currentPendingTransition(document);
    if (this.selectedScope().scopeCode === 'step' && (from !== 'in_progress' || to !== 'completed') &&
      (!current || current.fromStateCode !== from || current.toStateCode !== to)) return 'invalid';
    return transitions.some(transition => transition && transition !== current && transition.fromStateCode === from && transition.toStateCode === to) ? 'invalid' : 'valid';
  }
  private currentStepStatusesNamespace(document: WorkflowDefinitionDocument | null): string {
    return JSON.stringify(['step-statuses', document?.workflowCode, document?.definitionVersionCode, document?.packReference,
      this.resetContextStepNodeId ?? this.selectedScope().stepNodeId, this.viewContext?.() ?? this.ownerContext?.() ?? null]);
  }
  private readStepStatusesSources(document: WorkflowDefinitionDocument | null): string {
    const id = this.resetContextStepNodeId ?? this.selectedScope().stepNodeId;
    const nodes = readableArray(document?.nodes).filter(node => node?.id === id);
    return JSON.stringify(nodes.length === 1 ? [nodes[0].typeCode, nodes[0].statusConfiguration, nodes[0].stepProgressConfiguration] : null);
  }
  private stepStatusesScopeCurrent(): boolean {
    const document = this.currentStepStatusesDocument(), node = this.activeHumanStep;
    return this.stepStatusesWorkspace && !!this.ownerContext && !!document && !!node && !this.destroyRef.destroyed
      && !this.interactionBlocked && this.currentStepStatusesNamespace(document) === this.stepStatusesNamespace
      && JSON.stringify(this.ownerContext()) === this.externalStatusNavigationOwner
      && (typeof this.resetContextStepNodeId !== 'string' || this.resetContextStepNodeId === node.id)
      && readableArray(document.nodes).filter(item => item?.id === node.id && item.typeCode === 'human_step').length === 1;
  }
  private stepStatusesParentCurrent(): boolean {
    const view = this.workflowStatusesView?.();
    return this.stepStatusesScopeCurrent() && !!view && view === this.externalStatusNavigationView
      && (this.workflowStatusesViewState === undefined || this.workflowStatusesViewState === view)
      && JSON.stringify(this.document) === JSON.stringify(this.currentStepStatusesDocument())
      && !this.ownerViews.focusSuspended() && !this.hostElement.nativeElement.closest('[hidden],[inert]');
  }
  private stepStatusesChildView(view: StepStatusesView): string | null {
    return this.stepStatusesParentCurrent() && this.stepStatusesView() === view
      ? JSON.stringify([this.stepStatusesNamespace, this.activeHumanStep?.id, this.workflowStatusesView?.(), view, this.stepStatusesGeneration]) : null;
  }
  private stepStatusesNavigationCurrent(request: StepStatusesNavigation, view: StepStatusesView): boolean {
    return this.stepStatusesParentCurrent() && request.view === view && this.stepStatusesView() === view
      && request.owner === JSON.stringify(this.ownerContext?.()) && request.source === JSON.stringify(this.currentStepStatusesDocument())
      && request.context === this.workflowStatusesRequestContext();
  }
  private revealStepStatusesView(view: StepStatusesView): void {
    if (this.stepStatusesView() !== view) { this.stepStatusesGeneration++; this.stepStatusesView.set(view); }
    this.stepStatusesFocus++; this.ownerViews.remember(this.stepStatusesNamespace, view);
  }
  configureStepLabels(request: StepStatusesNavigation): void {
    if (!request || !this.stepStatusesNavigationCurrent(request, 'overview')) return;
    if (this.formDirty() || this.notificationEditorPending || this.lifecycleImpact()) {
      this.stepStatusesMessage.set('Finish or cancel the open completion-check or notification settings before opening step labels.'); return;
    }
    this.stepStatusesMessage.set(null); this.revealStepStatusesView('step-labels'); this.focusStepStatusOwner('labels');
  }
  configureStepBypass(request: StepStatusesNavigation): void {
    if (!request || !this.stepStatusesNavigationCurrent(request, 'overview')) return;
    if (this.formDirty() || this.notificationEditorPending || this.lifecycleImpact()) {
      this.stepStatusesMessage.set('Finish or cancel the open completion-check or notification settings before opening bypass.'); return;
    }
    this.stepStatusesMessage.set(null); this.revealStepStatusesView('bypass'); this.focusStepStatusOwner('progress');
  }
  backFromStepBypass(request: StepStatusesNavigation): void {
    if (!request || !this.stepStatusesNavigationCurrent(request, 'bypass')) return;
    if (this.formDirty() || this.notificationEditorPending || this.lifecycleImpact()) {
      this.stepStatusesMessage.set('Finish or cancel the open completion-check or notification settings before returning to statuses.'); return;
    }
    this.stepStatusesMessage.set(null); this.revealStepStatusesView('overview'); this.focusStepStatusOwner('configure-bypass');
  }
  backToStepStatuses(request: StepStatusesNavigation): void {
    if (!request || !this.stepStatusesNavigationCurrent(request, 'step-labels')) return;
    if (this.businessStatusDirty() || this.statusEditor?.hasPendingChanges()) {
      this.stepStatusesMessage.set('Finish or cancel the open step-label input before returning to statuses.'); this.focusStepStatusOwner('labels'); return;
    }
    if (this.formDirty() || this.notificationEditorPending || this.lifecycleImpact()) {
      this.stepStatusesMessage.set('Finish or cancel the open completion-check or notification settings before returning to statuses.'); return;
    }
    this.stepStatusesMessage.set(null); this.revealStepStatusesView('overview'); this.focusStepStatusOwner('configure');
  }
  private focusStepStatusOwner(kind: 'progress' | 'labels' | 'configure' | 'configure-bypass', path?: string): void {
    const request = ++this.stepStatusesFocus, owner = JSON.stringify(this.ownerContext?.()), source = JSON.stringify(this.currentStepStatusesDocument()),
      context = this.workflowStatusesRequestContext(), focused = this.hostElement.nativeElement.ownerDocument.activeElement;
    setTimeout(() => {
      const host = this.hostElement.nativeElement, active = host.ownerDocument.activeElement;
      if (request !== this.stepStatusesFocus || !this.stepStatusesParentCurrent() || owner !== JSON.stringify(this.ownerContext?.())
        || source !== JSON.stringify(this.currentStepStatusesDocument()) || context !== this.workflowStatusesRequestContext() || !host.isConnected) return;
      if (active !== focused && active !== host.ownerDocument.body && active !== host.ownerDocument.documentElement && active?.isConnected && !active.closest('[hidden],[inert]')) return;
      if (kind === 'labels') {
        if (this.statusEditor?.hasPendingChanges()) this.statusEditor.focusPendingChanges(true);
        else if (path) this.statusEditor?.focusSavedPath(path);
        else this.statusEditor?.focusHeading(true);
      }
      else if (kind === 'progress') this.progressEditor?.focusPendingChanges(true, path);
      else {
        const target = host.querySelector<HTMLElement>(kind === 'configure-bypass' ? '[data-open-step-bypass]' : '[data-open-step-labels]');
        if (!target || target.closest('[hidden],[inert]') || target.matches(':disabled')) return;
        const focusedRequest = request + (active === target ? 0 : 1); target.focus({ preventScroll: true });
        if (focusedRequest === this.stepStatusesFocus && host.ownerDocument.activeElement === target && this.stepStatusesParentCurrent()
          && owner === JSON.stringify(this.ownerContext?.()) && source === JSON.stringify(this.currentStepStatusesDocument())
          && context === this.workflowStatusesRequestContext() && target.isConnected && !target.closest('[hidden],[inert]')) target.scrollIntoView?.({ block: 'nearest', inline: 'nearest' });
      }
    });
  }
  @HostListener('focusin') rememberStepStatusesFocus(): void { this.stepStatusesFocus++; }
  capturePendingStepStatusInputs(): readonly { name: string; label: string; value: string }[] {
    return [...(this.progressEditor?.capturePendingProgressInputs() ?? []), ...(this.statusEditor?.capturePendingStatusInputs() ?? [])];
  }
  private settleStepStatuses(kind: 'progress' | 'labels', document: WorkflowDefinitionDocument | null, apply: () => boolean): boolean {
    if (!this.stepStatusesWorkspace) return apply();
    if (!document || !this.canAuthor || this.inspectionMode || !this.stepStatusesScopeCurrent() || this.stepStatusesSettlement
      || JSON.stringify(document) !== JSON.stringify(this.currentStepStatusesDocument())) return false;
    const child = kind === 'progress' ? this.progressEditor : this.statusEditor;
    if (!child) return false;
    if (!child.hasPendingChanges()) return true;
    const scopes = child.pendingEditorScopes(() => document);
    // Current invalid input needs its owner's Apply to surface the precise error.
    // Stale input remains guarded before the child can clear or write anything.
    if (scopes.some(scope => { const state = scope.state(); return state !== 'clean' && state !== 'valid' && state !== 'invalid'; })) return false;
    const token: StepStatusesSettlement = { kind, owner: JSON.stringify(this.ownerContext?.()), source: JSON.stringify(document), nodeId: this.activeHumanStep!.id, accepted: false, rejected: false };
    this.stepStatusesSettlement = token;
    try {
      const result = apply();
      const nodes = readableArray(document.nodes).filter(node => node?.id === token.nodeId);
      const noChange = kind === 'labels' && nodes.length === 1 && JSON.stringify(this.statusEditor?.config.statuses.length ? this.statusEditor.config : null)
        === JSON.stringify(nodes[0].statusConfiguration ?? null);
      return result && !token.rejected && (token.accepted || noChange);
    } finally { this.stepStatusesSettlement = null; }
  }
  private stepStatusesOwnedCurrent(kind: 'progress' | 'labels', event: WorkflowStepProgressOwnedDocumentChange | WorkflowStatusOwnedDocumentChange): WorkflowDefinitionDocument | null {
    const document = this.currentStepStatusesDocument(), token = this.stepStatusesSettlement;
    if (!this.stepStatusesWorkspace || !document || !this.canAuthor || this.inspectionMode || !this.stepStatusesScopeCurrent()
      || event.nodeId !== this.activeHumanStep?.id || event.owner !== JSON.stringify(this.ownerContext?.()) || event.source !== JSON.stringify(document)) return null;
    if (token) return token.kind === kind && token.nodeId === event.nodeId && token.owner === event.owner && token.source === event.source ? document : null;
    return this.stepStatusesParentCurrent() && (kind === 'progress' ? this.stepStatusesView() !== 'step-labels' : this.stepStatusesView() === 'step-labels') ? document : null;
  }
  onStepProgressOwnedChange(event: WorkflowStepProgressOwnedDocumentChange): void {
    const current = this.stepStatusesOwnedCurrent('progress', event), token = this.stepStatusesSettlement;
    if (!current || !this.progressEditor || event.view !== this.progressEditor.ownerView || event.view === 'full'
      || this.emittingProgressChange || this.requireProgressOwnerAcknowledgement && !this.progressEditor.hasPendingChanges()
      || JSON.stringify(event.draft) !== JSON.stringify(this.progressEditor.draftSnapshot)) { if (token) token.rejected = true; return; }
    try {
      const expected = applyWorkflowStepProgress(current, event.nodeId, event.draft.values, event.draft.intent,
        { contracts: this.routingContracts, loading: this.referenceLoading });
      if (JSON.stringify(expected) !== JSON.stringify(event.document)) { if (token) token.rejected = true; return; }
      if (!this.requireProgressOwnerAcknowledgement) { this.acceptStepStatusesOwnedDocument(expected, token); return; }
      const namespace = this.stepStatusesNamespace;
      const canonical: WorkflowStepProgressOwnedDocumentChange = { ...event, document: expected, draft: structuredClone(event.draft) };
      this.emittingProgressChange = canonical;
      try { this.progressOwnedDocumentChange.emit(canonical); }
      finally { this.emittingProgressChange = null; }
      const receipt = this.currentStepStatusesDocument();
      if (!receipt || receipt === current || JSON.stringify(receipt) !== JSON.stringify(expected)
        || event.owner !== JSON.stringify(this.ownerContext?.()) || namespace !== this.stepStatusesNamespace
        || this.currentStepStatusesNamespace(receipt) !== namespace
        || JSON.stringify(this.pendingStepProgressDraft) !== JSON.stringify(event.draft)) {
        if (token) token.rejected = true;
        return;
      }
      this.document = receipt; this.stepStatusesSavedSources = this.readStepStatusesSources(receipt);
      if (token) token.accepted = true;
    } catch { if (token) token.rejected = true; }
  }
  onStepLabelsOwnedChange(event: WorkflowStatusOwnedDocumentChange): void {
    const current = this.stepStatusesOwnedCurrent('labels', event), token = this.stepStatusesSettlement;
    if (!current || event.view !== this.statusEditor?.workspaceView) { if (token) token.rejected = true; return; }
    try {
      const nodes = readableArray(event.document.nodes).filter(node => node?.id === event.nodeId);
      if (nodes.length !== 1) { if (token) token.rejected = true; return; }
      const expected = replaceStatusConfiguration(current, event.nodeId, nodes[0].statusConfiguration ?? emptyStatusConfiguration());
      if (JSON.stringify(expected) !== JSON.stringify(event.document)) { if (token) token.rejected = true; return; }
      this.acceptStepStatusesOwnedDocument(expected, token);
    } catch { if (token) token.rejected = true; }
  }
  private acceptStepStatusesOwnedDocument(document: WorkflowDefinitionDocument, token: StepStatusesSettlement | null): void {
    if (token) token.accepted = true;
    this.document = document; this.stepStatusesSavedSources = this.readStepStatusesSources(document); this.documentChange.emit(document);
  }

  pendingEditorScopes(latestDocument: () => WorkflowDefinitionDocument | null = () => this.document): readonly WorkflowEditorPendingScope[] {
    const notificationOwner = JSON.stringify(this.currentPendingTransition(this.document));
    const notifications = () => this.currentPendingTransition(latestDocument());
    const bind = <T extends { pendingEditorScopes: (...args: never[]) => readonly WorkflowEditorPendingScope[] }>(
      id: string, owner: () => T | undefined, scopes: () => readonly WorkflowEditorPendingScope[], dirty: () => boolean,
    ) => workflowEditorPendingOwnerScopes({ id, owner, scopes, aggregatePending: dirty,
      canApply: () => this.canAuthor && !this.interactionBlocked,
      focus: scope => scope.focus(),
    });
    const recipients = () => (this.notificationEditor?.pendingEditorScopes(() => notifications()?.notifications ?? null) ?? []).map(scope => ({
      ...scope,
      state: (): WorkflowEditorBufferState => scope.state() === 'clean' ? 'clean' :
        !notifications() || JSON.stringify(notifications()) !== notificationOwner || this.pendingNotificationSource(latestDocument()) !== this.pendingNotificationBaseline ? 'stale' : scope.state(),
      fingerprint: () => JSON.stringify([notificationOwner, this.pendingNotificationBaseline, this.pendingNotificationSource(latestDocument()), notifications(), scope.fingerprint()]),
      apply: () => { const document = latestDocument(); if (!document || !notifications() || JSON.stringify(notifications()) !== notificationOwner || this.pendingNotificationSource(document) !== this.pendingNotificationBaseline) return false; this.document = document; return scope.apply?.() ?? false; },
    }));
    const childScopes = (kind: 'progress' | 'labels') => {
      const editor = kind === 'progress' ? this.progressEditor : this.statusEditor;
      return (editor?.pendingEditorScopes(latestDocument) ?? []).map(scope => this.stepStatusesWorkspace ? { ...scope,
        apply: scope.apply ? () => this.settleStepStatuses(kind, latestDocument(), () => scope.apply!()) : undefined,
        focus: () => { this.revealStepStatusesView(kind === 'progress' ? this.progressEditor?.pendingProgressView ?? 'overview' : 'step-labels'); this.focusStepStatusOwner(kind); },
      } : scope);
    };
    return [
      { id: 'status-change-input', label: 'status change input', state: () => this.pendingFormState(latestDocument()),
        fingerprint: () => JSON.stringify([this.formDirty(), this.isAddingTransition(), this.transitionFormValues, this.pendingFormBaseline, this.pendingFormSource(latestDocument()), this.canAuthor, this.interactionBlocked, this.referenceLoading, this.stateCodes]),
        apply: () => { const document = latestDocument(); if (!document || this.pendingFormState(document) !== 'valid') return false; this.document = document; this.applyTransition(); return !this.formDirty(); },
        discard: () => this.discardMainFormChanges(latestDocument()),
        focus: () => this.focusAfterRender(() => this.transitionEditorHeading) },
      { id: 'lifecycle-impact', label: 'lifecycle change awaiting Apply', kind: 'modal-candidate',
        state: () => !this.lifecycleImpact() ? 'clean' : JSON.stringify(this.readableNodes(latestDocument()).find(node => node.id === this.lifecycleImpact()!.nodeId)) !== this.lifecycleImpact()!.nodeSnapshot ? 'stale' : 'valid',
        fingerprint: () => JSON.stringify([this.lifecycleImpact(), latestDocument(), this.canAuthor, this.interactionBlocked]),
        discard: () => this.cancelLifecycleImpact(false), focus: () => this.focusAfterRender(() => this.lifecycleImpactHeading) },
      ...bind('transition-recipients', () => this.notificationEditor, recipients, () => this.notificationEditorPending),
      ...(this.stepStatusesWorkspace ? [
        ...bind('transition-progress', () => this.progressEditor, () => childScopes('progress'), () => this.progressSettingsDirty()),
        ...bind('transition-statuses', () => this.statusEditor, () => childScopes('labels'), () => this.businessStatusDirty()),
      ] : [
        ...bind('transition-statuses', () => this.statusEditor, () => childScopes('labels'), () => this.businessStatusDirty()),
        ...bind('transition-progress', () => this.progressEditor, () => childScopes('progress'), () => this.progressSettingsDirty()),
      ]),
    ];
  }

  discardMainFormChanges(document = this.document): void {
    if (document) this.document = document;
    if (!this.formDirty()) return;
    if (this.notificationEditorPending && (this.isAddingTransition() || !this.selectedRow)) {
      this.transitionFormValues = this.emptyFormValues();
      this.setFormDirty(false);
    } else this.restoreTransitionFormFromDocument();
  }

  requestDeactivate(accepted: () => void, cancelled: () => void): void {
    this.requestDiscardPending(accepted, cancelled);
  }

  discardPendingChanges(): void {
    if (this.businessStatusDirty()) this.statusEditor?.discardPendingChanges();
    if (this.progressSettingsDirty())
      this.progressEditor?.discardPendingChanges();
    if (this.lifecycleImpact()) this.cancelLifecycleImpact(false);
    if (this.notificationEditorPending) {
      this.notificationEditor?.discardPendingChanges();
      this.onNotificationPendingChangesChange(false);
    }

    if (!this.formDirty()) {
      return;
    }

    this.restoreTransitionFormFromDocument();
  }

  focusPendingChanges(): void {
    if (this.progressSettingsDirty()) {
      if (this.stepStatusesWorkspace) { this.revealStepStatusesView(this.progressEditor?.pendingProgressView ?? 'overview'); this.focusStepStatusOwner('progress'); return; }
      this.progressEditor?.focusPendingChanges();
      return;
    }
    if (this.businessStatusDirty()) {
      if (this.stepStatusesWorkspace) { this.revealStepStatusesView('step-labels'); this.focusStepStatusOwner('labels'); return; }
      this.statusEditor?.focusPendingChanges();
      return;
    }
    if (this.lifecycleImpact()) {
      this.focusAfterRender(() => this.lifecycleImpactHeading);
      return;
    }
    if (this.notificationEditorPending && this.notificationEditor) {
      this.notificationEditor.focusPendingChanges();
      return;
    }

    this.focusAfterRender(() => this.transitionEditorHeading);
  }

  focusCurrentStatusRules(): void {
    if (this.lifecycleImpact()) {
      this.focusPendingChanges();
      return;
    }
    this.focusAfterRender(() =>
      this.activeHumanStep
        ? this.effectiveLifecycleHeading
        : this.transitionEditorHeading,
    );
  }

  beginAddTransition(focusInspector = true): void {
    if (
      !this.canAuthor ||
      this.inspectionMode ||
      this.interactionBlocked ||
      this.notificationEditorPending ||
      this.referenceLoading ||
      !this.referenceDataReady ||
      !this.activeScopeDefinition?.isAuthorable
    ) {
      return;
    }

    this.requestDiscardPending(() => {
      const scope = this.selectedScope();
      if (this.activeHumanStep) {
        if (!this.canAddCompletionRule) return;
        this.prepareLifecycleImpact('add', null);
        return;
      }
      this.startAddTransition(
        scope.scopeCode,
        scope.stepNodeId ?? '',
        `Add a status change for ${this.scopeLabel(scope)}.`,
        false,
      );
      if (focusInspector) {
        this.focusAfterRender(() => this.transitionEditorHeading);
      }
    });
  }

  selectScope(
    scope: WorkflowDraftTransitionScope,
    focusInspector = false,
  ): void {
    if (this.interactionBlocked || this.notificationEditorPending) {
      return;
    }

    this.requestDiscardPending(() => {
      this.selectedScope.set({
        scopeCode: scope.scopeCode,
        stepNodeId: scope.stepNodeId,
      });
      this.selectFirstTransitionInScope(focusInspector);
    });
  }

  reviewTransition(
    row: WorkflowDraftTransitionRow,
    focusInspector = true,
  ): void {
    if (this.interactionBlocked || this.notificationEditorPending) {
      return;
    }
    const wasSelected = this.selectedRow?.key === row.key;
    this.requestDiscardPending(() => {
      const current = wasSelected
        ? this.selectedRow
        : this.rows.find(
            (candidate) =>
              candidate.scopeCode === row.scopeCode &&
              candidate.stepNodeId === row.stepNodeId &&
              (row.transition
                ? !!candidate.transition &&
                  this.transitionIdentity(candidate.transition) ===
                    this.transitionIdentity(row.transition)
                : candidate.index === row.index &&
                  candidate.transition === null),
          );
      if (!current) {
        this.showLocalError(
          'This saved status change is no longer available. Select its current entry again.',
        );
        return;
      }
      this.selectedScope.set({
        scopeCode: current.scopeCode,
        stepNodeId: current.stepNodeId,
      });
      this.loadSelection(current);
      if (this.activeHumanStep) this.customizationExpanded.set(true);
      if (focusInspector) {
        this.focusAfterRender(() => this.transitionEditorHeading);
      }
    });
  }

  onFormChange(values: unknown): void {
    if (
      !this.canAuthor ||
      this.interactionBlocked ||
      this.referenceLoading ||
      this.notificationEditorPending ||
      !this.referenceDataReady
    ) {
      return;
    }

    const nextValues = values as Partial<TransitionFormValues>;
    this.transitionFormValues = {
      ...this.transitionFormValues,
      fromStateCode:
        nextValues.fromStateCode ?? this.transitionFormValues.fromStateCode,
      toStateCode:
        nextValues.toStateCode ?? this.transitionFormValues.toStateCode,
    };
    this.setFormDirty(true);
    this.localError.set(null);
  }

  onActionButtonClick(button: ButtonConfig): void {
    switch (button.id) {
      case 'applyTransition':
        this.applyTransition();
        break;
      case 'resetTransition':
        this.resetTransition();
        break;
      case 'removeTransition':
        this.requestRemoveTransition();
        break;
      default:
        break;
    }
  }

  applyPendingChanges(latestDocument?: WorkflowDefinitionDocument): boolean {
    if (this.notificationEditorPending && this.pendingNotificationSource(latestDocument ?? this.document) !== this.pendingNotificationBaseline) {
      this.showLocalError('This notification owner changed while editing. Your recipient input is kept.');
      return false;
    }
    if (this.formDirty() && this.pendingFormSource(latestDocument ?? this.document) !== this.pendingFormBaseline) {
      this.showLocalError('This status change source changed while editing. Your input is kept.');
      return false;
    }
    if (this.progressSettingsDirty()) {
      if (
        !this.canAuthor ||
        this.interactionBlocked ||
        !this.settleStepStatuses('progress', latestDocument || this.currentStepStatusesDocument(), () => this.progressEditor?.applyPendingChanges(
          latestDocument || this.currentStepStatusesDocument() || undefined,
        ) ?? false)
      )
        return false;
      latestDocument = this.document || undefined;
    }
    if (this.businessStatusDirty()) {
      const beforeStatus = latestDocument || this.document;
      if (
        !this.canAuthor ||
        this.interactionBlocked ||
        !this.settleStepStatuses('labels', latestDocument || this.currentStepStatusesDocument(), () => this.statusEditor?.applyPendingChanges(
          latestDocument || this.currentStepStatusesDocument() || undefined,
        ) ?? false)
      )
        return false;
      latestDocument = this.document || undefined;
      // The supported status writer may raise the payload version. Accept that
      // local upgrade only when every other captured main-form source is intact.
      if (
        beforeStatus &&
        latestDocument &&
        this.formDirty() &&
        this.pendingFormSource({
          ...latestDocument,
          payloadSchemaVersion: beforeStatus.payloadSchemaVersion,
        }) === this.pendingFormBaseline
      )
        this.pendingFormBaseline = this.pendingFormSource(latestDocument);
    }
    if (this.lifecycleImpact()) {
      this.showLocalError(
        'Review the lifecycle change, then choose Apply change or Cancel change before saving or leaving Status rules.',
      );
      this.focusPendingChanges();
      return false;
    }
    if (!this.formDirty() && !this.notificationEditorPending) return true;
    if (!this.canAuthor || this.interactionBlocked) return false;
    const previous = this.selectedRow;
    if (latestDocument) this.document = latestDocument;
    if (!this.document || !this.resolveScope(this.selectedScope())) {
      this.showLocalError(
        'This status-change scope is no longer available. Your edits remain open.',
      );
      return false;
    }
    if (previous?.transition) {
      const matches = this.transitionsAt(
        previous.scopeCode,
        previous.stepNodeId,
      )
        .map((transition, index) => ({ transition, index }))
        .filter(
          ({ transition }) =>
            !!transition &&
            this.transitionIdentity(transition) ===
              this.transitionIdentity(previous.transition!),
        );
      if (matches.length !== 1) {
        this.showLocalError(
          'The selected status change is no longer available. Your edits remain open.',
        );
        return false;
      }
      this.selectedTransition.set({ ...previous, index: matches[0].index });
    }
    const capture = this.documentChange.subscribe(
      (value) => (this.document = value),
    );
    try {
      if (this.formDirty()) this.applyTransition();
      if (this.formDirty()) return false;
      if (this.notificationEditorPending) {
        if (!this.notificationEditor || !this.selectedRow?.transition)
          return false;
        this.notificationEditor.interactionBlocked = this.interactionBlocked;
        if (
          !this.notificationEditor.applyPendingChanges(
            this.selectedRow.transition.notifications ?? null,
          )
        )
          return false;
      }
      return !this.notificationEditorPending;
    } finally {
      capture.unsubscribe();
    }
  }

  applyTransition(): void {
    if (this.formDirty() && this.pendingFormSource(this.document) !== this.pendingFormBaseline) {
      this.showLocalError('This status change source changed while editing. Your input is kept.');
      return;
    }
    if (
      !this.document ||
      !this.canAuthor ||
      this.interactionBlocked ||
      this.referenceLoading ||
      !this.referenceDataReady ||
      !this.ensureFormValid()
    ) {
      return;
    }
    if (!this.isAddingTransition() && !this.selectedRow?.transition) {
      return;
    }

    const values = this.transitionFormValues;
    const activeScope = this.selectedScope();
    const fromStateCode = values.fromStateCode.trim();
    const toStateCode = values.toStateCode.trim();
    if (
      this.activeHumanStep &&
      (fromStateCode !== 'in_progress' || toStateCode !== 'completed')
    ) {
      const current = this.selectedRow?.transition;
      if (
        !current ||
        current.fromStateCode !== fromStateCode ||
        current.toStateCode !== toStateCode
      ) {
        this.showLocalError(
          'Work steps currently support completion from In progress to Completed. Other saved pairs are retained for inspection or removal.',
        );
        return;
      }
    }
    if (
      !this.stateCodes.includes(fromStateCode) ||
      !this.stateCodes.includes(toStateCode)
    ) {
      this.showLocalError(
        'Choose active current and next statuses from the authoring catalog.',
      );
      return;
    }

    const stepNodeId =
      activeScope.scopeCode === 'step' ? activeScope.stepNodeId : null;
    if (
      activeScope.scopeCode === 'step' &&
      this.humanStepNodes.filter((node) => node.id === stepNodeId).length !== 1
    ) {
      this.showLocalError(
        'Choose an available work step for the status change.',
      );
      return;
    }

    const initialSelection = this.selectedTransition();
    const selectedRow = initialSelection
      ? this.resolveSelection(initialSelection)
      : null;
    const selection =
      initialSelection && selectedRow
        ? { ...initialSelection, index: selectedRow.index }
        : initialSelection;
    const targetTransitions = this.transitionsAt(
      activeScope.scopeCode,
      stepNodeId,
    );
    const duplicateIndex = targetTransitions.findIndex(
      (transition) =>
        transition !== null &&
        this.transitionIdentity(transition) ===
          this.transitionIdentity({ fromStateCode, toStateCode }),
    );
    const selectedIndex =
      selection?.scopeCode === activeScope.scopeCode &&
      selection.stepNodeId === stepNodeId
        ? selection.index
        : -1;
    if (duplicateIndex >= 0 && duplicateIndex !== selectedIndex) {
      this.showLocalError(
        'That current and next status pair already exists at this scope.',
      );
      return;
    }

    const current = selection
      ? (this.resolveSelection(selection)?.transition ?? null)
      : null;
    const nextTransition: WorkflowAllowedTransition = current
      ? {
          ...current,
          fromStateCode,
          toStateCode,
        }
      : {
          fromStateCode,
          toStateCode,
          requiredDecisionKinds: null,
          requiredDecisions: null,
        };
    const nextIndex = selection?.index ?? targetTransitions.length;
    const nextDocument = this.replaceTransitions(
      activeScope.scopeCode,
      stepNodeId,
      selection
        ? targetTransitions.map((transition, index) =>
            index === selection.index ? nextTransition : transition,
          )
        : [...targetTransitions, nextTransition],
    );

    this.document = nextDocument;
    this.isAddingTransition.set(false);
    this.selectedTransition.set({
      scopeCode: activeScope.scopeCode,
      stepNodeId,
      index: nextIndex,
    });
    this.selectedTransitionIdentity = {
      scopeCode: activeScope.scopeCode,
      stepNodeId,
      fromStateCode,
      toStateCode,
    };
    this.transitionFormValues = {
      scopeCode: activeScope.scopeCode,
      stepNodeId: stepNodeId ?? '',
      fromStateCode,
      toStateCode,
    };
    this.transitionFormConfig = this.buildFormConfig(nextTransition);
    this.setFormDirty(false);
    this.localError.set(null);
    this.statusMessage.set(
      current
        ? 'Status change updated locally.'
        : 'Status change added locally.',
    );
    this.documentChange.emit(nextDocument);
  }

  resetTransition(): void {
    if (this.isAddingTransition()) {
      this.selectFirstTransitionInScope(false);
      this.statusMessage.set('New status change cancelled.');
      this.focusAfterRender(() => this.transitionEditorHeading);
      return;
    }

    const selection = this.selectedTransition();
    const row = selection ? this.resolveSelection(selection) : null;
    if (!row) {
      this.selectFirstTransitionInScope(false);
      return;
    }

    this.loadSelection(row);
    this.statusMessage.set('Unapplied status changes reset.');
    this.focusAfterRender(() => this.transitionEditorHeading);
  }

  focusTransitionPath(
    path: string,
    locationId: string | null,
    source: 'validation' | 'review' = 'validation',
  ): void {
    if (this.stepStatusesWorkspace && (path.includes('stepProgressConfiguration') || path.includes('.statusConfiguration'))) {
      const nodeId = locationId ?? stepNodeIdFromPersistedPath(path);
      if (nodeId && nodeId === this.activeHumanStep?.id) {
        const kind = path.includes('stepProgressConfiguration') ? 'progress' : 'labels';
        const message = kind === 'progress' ? 'Showing step progress settings.' : source === 'review' ? 'Showing saved custom statuses.' : 'Review these custom status settings.';
        this.statusMessage.set(message); this.stepStatusesMessage.set(message);
        this.revealStepStatusesView(kind === 'progress' ? path.includes('.bypass') ? 'bypass' : 'overview' : 'step-labels'); this.focusStepStatusOwner(kind, path); return;
      }
      if (nodeId) return;
    }
    if (path.includes('stepProgressConfiguration')) {
      const stepNodeId = locationId ?? stepNodeIdFromPersistedPath(path);
      if (!stepNodeId) return;
      this.requestDiscardPending(() => {
        this.selectedScope.set({ scopeCode: 'step', stepNodeId });
        this.clearSelection();
        this.statusMessage.set('Showing step progress settings.');
        setTimeout(() => this.progressEditor?.focusPendingChanges());
      });
      return;
    }
    if (path.includes('statusConfiguration')) {
      const stepNodeId = locationId ?? stepNodeIdFromPersistedPath(path);
      if (this.externalWorkflowStatuses && stepNodeId === null) {
        if (/^\$\.statusConfiguration(?:$|\.|\[)/.test(path)) this.requestExternalWorkflowStatuses(path, source);
        else this.statusMessage.set('This saved status diagnostic has no supported scope. Its original settings are preserved.');
        return;
      }
      this.requestDiscardPending(() => {
        this.selectedScope.set(
          stepNodeId
            ? { scopeCode: 'step', stepNodeId }
            : { scopeCode: 'workflow', stepNodeId: null },
        );
        this.clearSelection();
        this.statusMessage.set(
          source === 'review'
            ? 'Showing saved custom statuses.'
            : 'Review these custom status settings.',
        );
        setTimeout(() => this.statusEditor?.focusPendingChanges());
      });
      return;
    }
    if (source === 'review') {
      this.focusReviewedTransitionPath(path, locationId);
      return;
    }

    const isStepTransition = path.includes('.allowedTransitions');
    const scope: TransitionScopeSelection =
      isStepTransition && locationId
        ? { scopeCode: 'step', stepNodeId: locationId }
        : { scopeCode: 'workflow', stepNodeId: null };
    const location =
      isStepTransition && locationId
        ? this.scopeLabel(scope)
        : 'the whole workflow';
    this.requestDiscardPending(() => {
      this.selectedScope.set(scope);
      if (scope.scopeCode === 'step') this.customizationExpanded.set(true);
      const row = this.firstMeaningfulRow(this.activeRows);
      if (row) {
        this.loadSelection(row);
      } else {
        this.clearSelection();
      }
      this.statusMessage.set(
        `Validation identified a status-change issue for ${location}.`,
      );
      this.focusAfterRender(() =>
        row ? this.transitionEditorHeading : this.transitionSectionHeading,
      );
    });
  }

  private focusReviewedTransitionPath(
    path: string,
    locationId: string | null,
  ): void {
    const isStepTransition = path.includes('.allowedTransitions');
    const stepNodeId = locationId ?? stepNodeIdFromPersistedPath(path);
    const requestedScope: TransitionScopeSelection =
      isStepTransition && stepNodeId
        ? { scopeCode: 'step', stepNodeId }
        : { scopeCode: 'workflow', stepNodeId: null };
    const requestedScopeAvailable = this.resolveScope(requestedScope) !== null;
    const availableScope = requestedScopeAvailable
      ? requestedScope
      : { scopeCode: 'workflow' as const, stepNodeId: null };
    const location =
      isStepTransition && stepNodeId
        ? this.scopeLabel(requestedScope)
        : 'the whole workflow';

    this.requestDiscardPending(() => {
      this.selectedScope.set(availableScope);
      if (availableScope.scopeCode === 'step')
        this.customizationExpanded.set(true);
      const row = requestedScopeAvailable
        ? this.reviewedTransitionRow(path, requestedScope)
        : null;
      if (row) {
        this.loadSelection(row);
      } else {
        this.clearSelection();
      }
      this.statusMessage.set(
        row
          ? `Showing saved status-change behavior for ${location}.`
          : requestedScopeAvailable
            ? `That saved status change is no longer present for ${location}. Showing this status-change scope.`
            : `That saved status change belongs to ${location}, which is no longer available. Showing the status-change section.`,
      );
      this.focusAfterRender(() =>
        row ? this.transitionEditorHeading : this.transitionSectionHeading,
      );
    });
  }

  private reviewedTransitionRow(
    path: string,
    scope: TransitionScopeSelection,
  ): WorkflowDraftTransitionRow | null {
    const selector = persistedTransitionSelector(path);
    if (!selector) {
      return null;
    }

    const rows = this.rowsAtScope(scope);
    if (selector.index !== null) {
      return rows.find((row) => row.index === selector.index) ?? null;
    }

    return (
      rows.find(
        (row) =>
          row.transition?.fromStateCode === selector.fromStateCode &&
          row.transition.toStateCode === selector.toStateCode,
      ) ?? null
    );
  }

  focusStepTransitions(nodeId: string): void {
    this.requestDiscardPending(() => {
      const step = this.humanStepNodes.find((node) => node.id === nodeId);
      const stepLabel = step?.displayName || step?.stepCode || nodeId;
      this.selectedScope.set({
        scopeCode: 'step',
        stepNodeId: nodeId,
      });
      const row = this.firstMeaningfulRow(this.activeRows);
      let focusEditor = false;
      if (row) {
        this.loadSelection(row);
        this.statusMessage.set(`Showing status rules for ${stepLabel}.`);
        focusEditor = true;
      } else {
        this.clearSelection();
        this.statusMessage.set(
          this.activeLifecycleUnavailable ? `${stepLabel} has an unavailable saved lifecycle collection. Its original content is preserved.`
            : `${stepLabel} uses the platform default lifecycle. The completion action is available from In progress to Completed.`,
        );
      }
      this.focusAfterRender(() =>
        this.activeHumanStep
          ? this.effectiveLifecycleHeading
          : focusEditor
            ? this.transitionEditorHeading
            : this.transitionSectionHeading,
      );
    });
  }

  transitionDecisionCount(
    transition: WorkflowAllowedTransition | null,
  ): number {
    if (!transition) {
      return 0;
    }

    const requirementKeys = new Set<string>();
    let malformedCount = 0;
    readableArray(transition.requiredDecisions).forEach((decision) => {
      if (!decision) {
        malformedCount += 1;
        return;
      }
      requirementKeys.add(this.decisionRequirementKey(decision));
    });
    readableArray(transition.requiredDecisionKinds).forEach((decisionKind) => {
      if (
        typeof decisionKind !== 'string' ||
        decisionKind.trim().length === 0
      ) {
        malformedCount += 1;
        return;
      }
      const legacyRequirement: WorkflowDecisionRequirement = {
        decisionKind,
        requiredOutcomeCode: 'approved',
        reaction: {
          reactionCode: 'permit',
          targetStateCode: null,
        },
        eligibleRoleCodes: null,
        ownerQueueCode: null,
      };
      requirementKeys.add(this.decisionRequirementKey(legacyRequirement));
    });
    return requirementKeys.size + malformedCount;
  }

  applyTransitionNotifications(
    notifications: Array<WorkflowNotificationRule | null> | null,
  ): void {
    const selection = this.selectedTransition();
    const row = this.selectedRow;
    if (!selection || !row?.transition || !this.document || !this.canAuthor) {
      return;
    }
    const transitions = this.transitionsAt(
      selection.scopeCode,
      selection.stepNodeId,
    );
    let nextDocument = this.replaceTransitions(
      selection.scopeCode,
      selection.stepNodeId,
      transitions.map((transition, index) =>
        index === selection.index && transition
          ? { ...transition, notifications }
          : transition,
      ),
    );
    if ((notifications?.length ?? 0) > 0) {
      nextDocument = {
        ...nextDocument,
        payloadSchemaVersion: requireWorkflowPayloadVersion(
          nextDocument.payloadSchemaVersion,
          '1.1',
        ),
      };
    }
    this.statusMessage.set(
      (notifications?.filter(Boolean).length ?? 0) > 0
        ? 'Notification recipients updated in the working draft.'
        : 'Notifications removed from this status change in the working draft.',
    );
    this.documentChange.emit(nextDocument);
  }

  onNotificationPendingChangesChange(value: boolean): void {
    if (this.notificationFormDirty === value) {
      return;
    }
    this.pendingNotificationBaseline = value ? this.pendingNotificationSource(this.document) : null;
    this.notificationFormDirty = value;
    this.emitPendingChanges();
  }

  transitionAriaLabel(row: WorkflowDraftTransitionRow): string {
    if (!row.transition) {
      return `Review invalid status-change entry ${row.index + 1} for ${row.stepLabel}`;
    }
    return (
      `Review ${this.stateLabel(row.transition.fromStateCode)} to ` +
      `${this.stateLabel(row.transition.toStateCode)} status change for ${row.stepLabel}`
    );
  }

  stateLabel(code: unknown): string {
    if (typeof code !== 'string' || code.trim().length === 0) {
      return 'Not specified';
    }
    const words = code.replace(/[_-]+/g, ' ').trim();
    return words.charAt(0).toUpperCase() + words.slice(1);
  }

  requestDecisionRules(): void {
    if (!this.applyPendingChanges(this.document ?? undefined)) {
      this.focusPendingChanges();
      return;
    }
    const selection = this.selectedTransition();
    const row = this.selectedRow;
    if (
      !selection ||
      !row?.transition ||
      this.interactionBlocked ||
      this.notificationEditorPending ||
      (!!this.activeHumanStep?.stepProgressConfiguration &&
        !this.isCompletionTransition(row.transition))
    ) {
      return;
    }
    this.decisionRulesRequested.emit({
      scopeCode: selection.scopeCode,
      stepNodeId: selection.stepNodeId,
      transitionIndex: row.index,
    });
  }

  requestCompletionDecisionRules(): void {
    if (
      this.interactionBlocked ||
      !this.applyPendingChanges(this.document ?? undefined)
    ) {
      this.focusPendingChanges();
      return;
    }
    const row = this.completionRow;
    if (!row) return;
    this.loadSelection(row);
    this.requestDecisionRules();
  }

  requestStepDecisionRules(): void {
    if (
      this.interactionBlocked ||
      !this.applyPendingChanges(this.document ?? undefined)
    ) {
      this.focusPendingChanges();
      return;
    }
    const step = this.activeHumanStep;
    if (
      step &&
      this.readableNodes().filter((node) => node.id === step.id).length === 1
    ) {
      this.stepDecisionRulesRequested.emit(step.id);
    }
  }

  requestCompletionRequirements(): void {
    if (
      this.interactionBlocked ||
      !this.applyPendingChanges(this.document ?? undefined)
    ) {
      this.focusPendingChanges();
      return;
    }
    const step = this.activeHumanStep;
    if (
      step &&
      this.readableNodes().filter((node) => node.id === step.id).length === 1
    ) {
      this.completionRequirementsRequested.emit(step.id);
    }
  }

  requestNotifications(): void {
    const owner = this.selectedRow;
    if (
      !owner?.transition ||
      owner.scopeCode !== 'step' ||
      !owner.stepNodeId ||
      this.interactionBlocked
    )
      return;
    if (!this.applyPendingChanges(this.document ?? undefined)) {
      this.focusPendingChanges();
      return;
    }
    if (
      this.selectedScope().scopeCode !== owner.scopeCode ||
      this.selectedScope().stepNodeId !== owner.stepNodeId
    ) {
      this.showLocalError(
        'The selected status-change context changed. Select the event again to open its notifications.',
      );
      return;
    }
    const nodes =
      this.readableNodes().filter((node) => node.id === owner.stepNodeId) ?? [];
    const current = this.selectedRow?.transition;
    const matches = readableArray(nodes[0]?.allowedTransitions)
      .map((transition, index) => ({ transition, index }))
      .filter(
        ({ transition }) =>
          !!current &&
          !!transition &&
          this.transitionIdentity(transition) ===
            this.transitionIdentity(current),
      );
    if (nodes.length !== 1 || matches.length !== 1) {
      this.showLocalError(
        'This status change cannot be identified uniquely. Repair the saved entries before opening its notifications.',
      );
      return;
    }
    this.notificationContextRequested.emit({
      nodeId: owner.stepNodeId,
      path: `$.nodes[id=${owner.stepNodeId}].allowedTransitions[${matches[0].index}]`,
    });
  }

  private requestRemoveTransition(): void {
    const selection = this.selectedTransition();
    const row = selection ? this.resolveSelection(selection) : null;
    if (
      !selection ||
      !row ||
      !this.document ||
      !this.canAuthor ||
      this.interactionBlocked ||
      this.referenceLoading ||
      this.notificationEditorPending
    ) {
      return;
    }

    if (this.activeHumanStep) {
      this.prepareLifecycleImpact('remove', row);
      return;
    }

    const decisionCount = this.transitionDecisionCount(row.transition);
    const decisionWarning =
      decisionCount > 0
        ? ` It also removes ${decisionCount} preserved decision gate${decisionCount === 1 ? '' : 's'}.`
        : '';
    const notificationCount =
      row.transition?.notifications?.filter(Boolean).length ?? 0;
    const notificationWarning =
      notificationCount > 0
        ? ` It also removes ${notificationCount} notification rule${notificationCount === 1 ? '' : 's'}.`
        : '';
    const messageWarning =
      row.transition?.notificationMessage ||
      row.transition?.requiredDecisions?.some(
        (decision) => decision?.notificationMessage,
      )
        ? ' It also removes the attached notification messages.'
        : '';
    const lifecycleWarning = this.activeHumanStep
      ? this.activeRows.length === 1
        ? ' Removing the last explicit rule restores the platform default lifecycle, including completion.'
        : this.isCompletionTransition(row.transition)
          ? ' The remaining explicit rules will block work-step completion.'
          : ''
      : '';
    const changeDescription = row.transition
      ? `${this.stateLabel(row.transition.fromStateCode)} to ` +
        `${this.stateLabel(row.transition.toStateCode)}`
      : `Invalid status-change entry ${row.index + 1}`;
    const removalContext = () => JSON.stringify([this.document && [this.document.workflowCode, this.document.definitionVersionCode, this.document.packReference],
      this.resetVersion, this.resetContextStepNodeId, this.selectedScope(), this.selectedTransition(), this.selectedTransitionIdentity,
      this.canAuthor, this.interactionBlocked, this.inspectionMode, this.referenceLoading, this.formDirty(), this.notificationEditorPending,
      this.businessStatusDirty(), this.progressSettingsDirty(), this.lifecycleImpact(), this.transitionsAt(selection.scopeCode, selection.stepNodeId)]);
    const removalSnapshot = removalContext();
    void this.confirmLocally({
        heading: row.transition
          ? 'Remove this status change?'
          : 'Remove this invalid entry?',
        message: `${changeDescription} will be removed from ${row.stepLabel}.${decisionWarning}${notificationWarning}${messageWarning}${lifecycleWarning}`,
        confirmText: row.transition
          ? 'Remove status change'
          : 'Remove invalid entry',
        cancelText: row.transition
          ? 'Keep status change'
          : 'Keep invalid entry',
      })
      .then((confirmed) => {
        if (this.destroyRef.destroyed) return;
        if (!confirmed) {
          this.focusAfterRender(() => this.transitionEditorHeading);
          return;
        }
        if (removalContext() !== removalSnapshot) {
          this.showLocalError('This status change, attached content or editor context changed. Review it again before removing it.');
          return;
        }
        if (
          !this.document ||
          !this.canAuthor ||
          this.interactionBlocked ||
          this.notificationEditorPending
        ) {
          this.focusPendingChanges();
          return;
        }

        const transitions = this.transitionsAt(
          selection.scopeCode,
          selection.stepNodeId,
        );
        const matches = transitions
          .map((transition, index) => ({ transition, index }))
          .filter(({ transition, index }) =>
            row.transition
              ? !!transition &&
                this.transitionIdentity(transition) ===
                  this.transitionIdentity(row.transition)
              : index === selection.index && transition === null,
          );
        if (
          matches.length !== 1 ||
          (selection.scopeCode === 'step' &&
            this.readableNodes().filter(
              (node) => node.id === selection.stepNodeId,
            ).length !== 1)
        ) {
          this.showLocalError(
            'This status change is no longer uniquely available. Select the saved entry again before removing it.',
          );
          return;
        }
        const nextTransitions = transitions.filter(
          (_, index) => index !== matches[0].index,
        );
        const nextDocument = this.replaceTransitions(
          selection.scopeCode,
          selection.stepNodeId,
          nextTransitions,
        );
        this.document = nextDocument;
        this.selectFirstTransitionFrom(nextTransitions);
        this.statusMessage.set(
          row.transition
            ? 'Status change and its attached decision metadata removed locally.'
            : 'Invalid status-change entry removed locally.',
        );
        this.documentChange.emit(nextDocument);
        this.focusAfterRender(() => this.transitionEditorHeading);
      });
  }

  private prepareLifecycleImpact(
    operation: 'add' | 'remove',
    row: WorkflowDraftTransitionRow | null,
  ): void {
    const step = this.activeHumanStep;
    if (!step || !this.document || this.lifecycleImpact()) return;
    const current = this.transitionsAt('step', step.id);
    const after =
      operation === 'add'
        ? [
            ...current,
            {
              fromStateCode: 'in_progress',
              toStateCode: 'completed',
              requiredDecisionKinds: null,
              requiredDecisions: null,
            },
          ]
        : current.filter((_, index) => index !== row?.index);
    this.lifecycleImpact.set({
      operation,
      nodeId: step.id,
      workflowCode: this.document.workflowCode,
      definitionVersionCode: this.document.definitionVersionCode,
      resetVersion: this.resetVersion,
      contextNodeId: this.resetContextStepNodeId,
      nodeSnapshot: JSON.stringify(step),
      selectionKey: this.selectedKey,
      before: this.lifecycleDescription(current),
      after: this.lifecycleDescription(after),
      attachedDecisionCount: this.transitionDecisionCount(
        row?.transition ?? null,
      ),
      attachedNotificationCount:
        row?.transition?.notifications?.filter(Boolean).length ?? 0,
      attachedMessageCount:
        (row?.transition?.notificationMessage ? 1 : 0) +
        (row?.transition?.requiredDecisions?.filter(
          (decision) => !!decision?.notificationMessage,
        ).length ?? 0),
      transitionIndex: row?.index ?? null,
    });
    this.localError.set(null);
    this.statusMessage.set(null);
    this.emitPendingChanges();
    this.focusPendingChanges();
  }

  applyLifecycleImpact(): void {
    const preview = this.lifecycleImpact();
    if (!preview || !this.canApplyLifecycleImpact) {
      this.showLocalError(
        'The step, selected rule, or authoring access changed. Cancel this preview and review the current lifecycle before trying again.',
      );
      return;
    }
    const current = this.transitionsAt('step', preview.nodeId);
    const transition: WorkflowAllowedTransition = {
      fromStateCode: 'in_progress',
      toStateCode: 'completed',
      requiredDecisionKinds: null,
      requiredDecisions: null,
    };
    const next =
      preview.operation === 'add'
        ? [...current, transition]
        : current.filter((_, index) => index !== preview.transitionIndex);
    const nextDocument = this.replaceTransitions('step', preview.nodeId, next);
    this.lifecycleImpact.set(null);
    this.document = nextDocument;
    if (preview.operation === 'add') {
      this.loadSelection(
        this.toRow(this.selectedScope(), current.length, transition),
      );
    } else {
      this.selectFirstTransitionFrom(next);
    }
    this.emitPendingChanges();
    this.localError.set(null);
    this.statusMessage.set(
      preview.operation === 'add'
        ? 'Completion rule added locally. Save draft to keep this lifecycle change.'
        : 'Status rule and its attached configuration removed locally. Save draft to keep this lifecycle change.',
    );
    this.documentChange.emit(nextDocument);
    this.focusCurrentStatusRules();
  }

  cancelLifecycleImpact(focus = true): void {
    if (!this.lifecycleImpact()) return;
    this.lifecycleImpact.set(null);
    this.localError.set(null);
    this.statusMessage.set(
      'Lifecycle change cancelled. The working draft is unchanged.',
    );
    this.emitPendingChanges();
    if (focus) this.focusCurrentStatusRules();
  }

  private lifecycleDescription(
    transitions: Array<WorkflowAllowedTransition | null>,
  ): string {
    if (this.activeLifecycleUnavailable) return 'The saved lifecycle collection is unavailable. Its original configuration is preserved.';
    const completion =
      transitions.find((transition) =>
        this.isCompletionTransition(transition),
      ) ?? null;
    const checkCount = this.effectiveDecisionCount(completion);
    const checks = this.effectiveDecisionCollectionsOpaque(completion) ? 'Decision-check totals cannot be established from the retained collections.'
      : `${checkCount} distinct decision check${checkCount === 1 ? ' applies' : 's apply'} to completion. Completion requirements still apply.`;
    if (transitions.length === 0) {
      return `Platform default lifecycle. The step starts automatically in In progress; its completion action is available from In progress to Completed. ${checks}`;
    }
    return `${transitions.length} explicit rule${transitions.length === 1 ? ' replaces' : 's replace'} the platform defaults. The step starts automatically in In progress; its completion action is ${completion ? 'available' : 'unavailable'}.${completion ? ` ${checks}` : ''}`;
  }

  private loadSelection(row: WorkflowDraftTransitionRow): void {
    this.isAddingTransition.set(false);
    this.selectedTransition.set({
      scopeCode: row.scopeCode,
      stepNodeId: row.stepNodeId,
      index: row.index,
    });
    this.selectedTransitionIdentity = row.transition
      ? {
          scopeCode: row.scopeCode,
          stepNodeId: row.stepNodeId,
          fromStateCode: row.transition.fromStateCode,
          toStateCode: row.transition.toStateCode,
        }
      : null;
    if (!row.transition) {
      this.transitionFormValues = {
        scopeCode: row.scopeCode,
        stepNodeId: row.stepNodeId ?? '',
        fromStateCode: '',
        toStateCode: '',
      };
      this.transitionFormConfig = [];
      this.setFormDirty(false);
      this.localError.set(null);
      this.statusMessage.set(this.hasOpaqueTransitionCollections ? this.opaqueCollectionMessage : null);
      return;
    }
    this.transitionFormValues = {
      scopeCode: row.scopeCode,
      stepNodeId: row.stepNodeId ?? '',
      fromStateCode: row.transition.fromStateCode,
      toStateCode: row.transition.toStateCode,
    };
    this.transitionFormConfig = this.buildFormConfig(row.transition);
    this.setFormDirty(false);
    this.localError.set(null);
    this.statusMessage.set(this.hasOpaqueTransitionCollections ? this.opaqueCollectionMessage : null);
  }

  private resolveSelection(
    selection: TransitionSelection,
  ): WorkflowDraftTransitionRow | null {
    const rows = this.rows.filter(
      (row) =>
        row.scopeCode === selection.scopeCode &&
        row.stepNodeId === selection.stepNodeId,
    );
    const identity = this.selectedTransitionIdentity;
    if (
      identity?.scopeCode === selection.scopeCode &&
      identity.stepNodeId === selection.stepNodeId
    ) {
      const matches = rows.filter(
        (row) =>
          !!row.transition &&
          this.transitionIdentity(row.transition) ===
            this.transitionIdentity(identity),
      );
      if (matches.length === 1) return matches[0];
      if (matches.length === 0) return null;
    }
    return rows.find((row) => row.index === selection.index) ?? null;
  }

  private resolveScope(
    selection: TransitionScopeSelection,
  ): WorkflowDraftTransitionScope | null {
    return (
      this.scopes.find(
        (scope) =>
          scope.scopeCode === selection.scopeCode &&
          scope.stepNodeId === selection.stepNodeId,
      ) ?? null
    );
  }

  private transitionsAt(
    scopeCode: TransitionScopeCode,
    stepNodeId: string | null,
  ): Array<WorkflowAllowedTransition | null> {
    if (scopeCode === 'workflow') {
      return readableArray(this.document?.workflowTransitions);
    }
    return readableArray(this.readableNodes().find((node) => node.id === stepNodeId)?.allowedTransitions);
  }

  private replaceTransitions(
    scopeCode: TransitionScopeCode,
    stepNodeId: string | null,
    transitions: Array<WorkflowAllowedTransition | null>,
  ): WorkflowDefinitionDocument {
    const document = this.document!;
    if (scopeCode === 'workflow') {
      const useNullCollection =
        transitions.length === 0 && this.initiallyNullWorkflowCollection;
      return {
        ...document,
        workflowTransitions: useNullCollection ? null : transitions,
      };
    }

    const useNullCollection =
      transitions.length === 0 &&
      stepNodeId !== null &&
      this.initiallyNullStepCollections.has(stepNodeId);
    return {
      ...document,
      nodes: document.nodes.map((node) =>
        node.id === stepNodeId
          ? {
              ...node,
              allowedTransitions: useNullCollection ? null : transitions,
            }
          : node,
      ),
    };
  }

  private buildFormConfig(
    existingTransition?: WorkflowAllowedTransition,
  ): InputConfig[] {
    const activeStateOptions = this.stateOptions(existingTransition);
    return [
      {
        name: 'fromStateCode',
        type: EzFormInputType.SELECT,
        label: 'Current status',
        row: 1,
        width: '1x',
        options: activeStateOptions,
        validation: [Validators.required],
      },
      {
        name: 'toStateCode',
        type: EzFormInputType.SELECT,
        label: 'Allowed next status',
        row: 1,
        width: '1x',
        options: activeStateOptions,
        validation: [Validators.required],
      },
    ];
  }

  private stateOptions(
    existingTransition?: WorkflowAllowedTransition,
  ): InputSelectOption[] {
    const codes = [...this.stateCodes];
    if (existingTransition) {
      if (!codes.includes(existingTransition.fromStateCode)) {
        codes.push(existingTransition.fromStateCode);
      }
      if (!codes.includes(existingTransition.toStateCode)) {
        codes.push(existingTransition.toStateCode);
      }
    }
    return codes.map((stateCode) => ({
      value: stateCode,
      viewValue: this.stateCodes.includes(stateCode)
        ? this.stateLabel(stateCode)
        : `${this.stateLabel(stateCode)} (not active)`,
    }));
  }

  private rebuildFormConfig(): void {
    const selection = this.selectedTransition();
    const existing = selection
      ? (this.resolveSelection(selection)?.transition ?? undefined)
      : undefined;
    if (selection || this.isAddingTransition()) {
      this.transitionFormConfig = this.buildFormConfig(existing);
    }
  }

  private requestDiscardPending(
    action: () => void,
    cancelled: () => void = () => undefined,
    latestDocument: WorkflowDefinitionDocument | null = this.document,
  ): void {
    if (this.destroyRef.destroyed || this.localConfirmationInProgress || this.ownerViews.focusSuspended()) { cancelled(); return; }
    if (this.businessStatusDirty() || this.progressSettingsDirty()) {
      if (!this.applyPendingChanges(latestDocument ?? undefined)) {
        this.focusPendingChanges();
        cancelled();
        return;
      }
    }
    if (this.lifecycleImpact()) {
      this.focusPendingChanges();
      cancelled();
      return;
    }
    if (this.notificationEditorPending) {
      this.focusPendingChanges();
      cancelled();
      return;
    }
    if (!this.formDirty()) {
      action();
      return;
    }
    if (this.applyPendingChanges(latestDocument ?? undefined)) action();
    else {
      this.focusPendingChanges();
      cancelled();
    }
  }

  private restoreTransitionFormFromDocument(): void {
    if (this.isAddingTransition()) {
      this.selectFirstTransitionInScope(false);
      return;
    }

    const selection = this.selectedTransition();
    const row = selection ? this.resolveSelection(selection) : null;
    if (row) {
      this.loadSelection(row);
      return;
    }

    this.selectFirstTransitionInScope(false);
  }

  private captureInitialCollectionShapes(): void {
    this.initiallyNullWorkflowCollection =
      this.document?.workflowTransitions === null;
    this.initiallyNullStepCollections.clear();
    this.captureNewNullCollectionShapes();
  }

  private captureNewNullCollectionShapes(): void {
    if (this.document?.workflowTransitions === null) {
      this.initiallyNullWorkflowCollection = true;
    }
    for (const node of this.readableNodes()) {
      if (node.allowedTransitions === null) {
        this.initiallyNullStepCollections.add(node.id);
      }
    }
  }

  private startAddTransition(
    scopeCode: TransitionScopeCode,
    stepNodeId: string,
    statusMessage: string,
    formDirty: boolean,
  ): void {
    const firstState = this.stateCodes.includes('in_progress')
      ? 'in_progress'
      : '';
    const secondState = this.stateCodes.includes('completed')
      ? 'completed'
      : '';
    this.isAddingTransition.set(true);
    this.selectedTransition.set(null);
    this.transitionFormValues = {
      scopeCode,
      stepNodeId: scopeCode === 'step' ? stepNodeId : '',
      fromStateCode: firstState,
      toStateCode: secondState,
    };
    this.transitionFormConfig = this.buildFormConfig();
    this.setFormDirty(formDirty);
    this.localError.set(null);
    this.statusMessage.set(statusMessage);
  }

  private resetFromDocument(): void {
    this.clearEditor();
    const requestedScope =
      this.resetContextStepNodeId === undefined
        ? null
        : this.resetContextStepNodeId === null
          ? this.scopes.find((scope) => scope.scopeCode === 'workflow')
          : this.scopes.find(
              (scope) =>
                scope.scopeCode === 'step' &&
                scope.stepNodeId === this.resetContextStepNodeId,
            );
    const meaningfulScope = this.scopes.find((scope) =>
      this.rowsAtScope(scope).some((row) => row.transition !== null),
    );
    const populatedScope = this.scopes.find(
      (scope) => this.rowsAtScope(scope).length > 0,
    );
    const scope =
      requestedScope ?? meaningfulScope ?? populatedScope ?? this.scopes[0];
    if (!scope) {
      return;
    }
    this.selectedScope.set({
      scopeCode: scope.scopeCode,
      stepNodeId: scope.stepNodeId,
    });
    this.selectFirstTransitionInScope(false);
  }

  private clearEditor(): void {
    const hadImpact = !!this.lifecycleImpact();
    this.lifecycleImpact.set(null);
    this.customizationExpanded.set(false);
    this.selectedScope.set({
      scopeCode: 'workflow',
      stepNodeId: null,
    });
    this.clearSelection();
    if (hadImpact) this.emitPendingChanges();
    this.statusMessage.set(null);
  }

  private clearSelection(): void {
    this.isAddingTransition.set(false);
    this.selectedTransition.set(null);
    this.selectedTransitionIdentity = null;
    this.transitionFormValues = this.emptyFormValues();
    this.transitionFormConfig = [];
    this.setFormDirty(false);
    this.onNotificationPendingChangesChange(false);
    this.localError.set(null);
  }

  private selectFirstTransitionInScope(focusInspector: boolean): void {
    this.clearSelection();
    const row = this.firstMeaningfulRow(this.activeRows);
    if (row) {
      this.loadSelection(row);
    }
    if (focusInspector) {
      this.focusAfterRender(() => this.transitionEditorHeading);
    }
  }

  private selectFirstTransitionFrom(
    transitions: Array<WorkflowAllowedTransition | null>,
  ): void {
    this.clearSelection();
    const meaningfulIndex = transitions.findIndex(
      (transition) => transition !== null,
    );
    const index =
      meaningfulIndex >= 0 ? meaningfulIndex : transitions.length > 0 ? 0 : -1;
    if (index < 0) {
      return;
    }
    const scope = this.selectedScope();
    this.loadSelection(this.toRow(scope, index, transitions[index] ?? null));
  }

  private firstMeaningfulRow(
    rows: WorkflowDraftTransitionRow[],
  ): WorkflowDraftTransitionRow | null {
    return rows.find((row) => row.transition !== null) ?? rows[0] ?? null;
  }

  private rowsAtScope(
    scope: TransitionScopeSelection,
  ): WorkflowDraftTransitionRow[] {
    return this.rows.filter(
      (row) =>
        row.scopeCode === scope.scopeCode &&
        row.stepNodeId === scope.stepNodeId,
    );
  }

  private toScope(
    selection: TransitionScopeSelection,
  ): WorkflowDraftTransitionScope {
    const rows = this.rowsAtScope(selection);
    const node =
      selection.scopeCode === 'step'
        ? this.readableNodes().find(
            (candidate) => candidate.id === selection.stepNodeId,
          )
        : null;
    const isAuthorable =
      selection.scopeCode === 'workflow' || node?.typeCode === 'human_step';
    return {
      ...selection,
      key: this.scopeKey(selection.scopeCode, selection.stepNodeId),
      label: this.scopeLabel(selection),
      detail:
        selection.scopeCode === 'workflow'
          ? 'Workflow status'
          : isAuthorable
            ? 'Work step status'
            : `${this.nodeTypeLabel(node)} · validation evidence`,
      isAuthorable,
      transitionCount: rows.length,
      decisionRuleCount: rows.reduce(
        (count, row) => count + this.transitionDecisionCount(row.transition),
        0,
      ),
    };
  }

  private toRow(
    selection: TransitionScopeSelection,
    index: number,
    transition: WorkflowAllowedTransition | null,
  ): WorkflowDraftTransitionRow {
    const stepLabel = this.scopeLabel(selection);
    return {
      ...selection,
      key:
        selection.scopeCode === 'workflow'
          ? `workflow:${index}`
          : `step:${selection.stepNodeId}:${index}`,
      index,
      scopeLabel:
        selection.scopeCode === 'workflow' ? 'Whole workflow' : 'Work step',
      stepLabel,
      transition,
    };
  }

  private scopeKey(
    scopeCode: TransitionScopeCode,
    stepNodeId: string | null,
  ): string {
    return scopeCode === 'workflow' ? 'workflow' : `step:${stepNodeId}`;
  }

  private scopeLabel(selection: TransitionScopeSelection): string {
    if (selection.scopeCode === 'workflow') {
      return 'Whole workflow';
    }
    const node = this.readableNodes().find(
      (candidate) => candidate.id === selection.stepNodeId,
    );
    return (
      node?.displayName || node?.stepCode || selection.stepNodeId || 'Work step'
    );
  }

  private decisionRequirementKey(
    decision: WorkflowDecisionRequirement,
  ): string {
    return [
      decision.decisionKind,
      decision.requiredOutcomeCode ?? 'approved',
      decision.reaction?.reactionCode ?? 'permit',
      decision.reaction?.targetStateCode ?? '',
    ]
      .map((value) =>
        typeof value === 'string' ? value.trim().toLowerCase() : '',
      )
      .join('|');
  }

  private transitionIdentity(
    transition: Pick<
      WorkflowAllowedTransition,
      'fromStateCode' | 'toStateCode'
    >,
  ): string {
    return JSON.stringify(
      [transition.fromStateCode, transition.toStateCode].map((code) =>
        typeof code === 'string' ? code.trim().toLowerCase() : '',
      ),
    );
  }

  private stepDecisionRequirements(): WorkflowAllowedTransition {
    return {
      fromStateCode: 'in_progress',
      toStateCode: 'completed',
      requiredDecisionKinds: null,
      requiredDecisions: this.activeHumanStep?.requiredDecisions ?? null,
    };
  }

  private ownerDecisionRequirements(): WorkflowAllowedTransition {
    return this.selectedScope().scopeCode === 'workflow'
      ? {
          fromStateCode: 'in_progress',
          toStateCode: 'completed',
          requiredDecisionKinds: null,
          requiredDecisions: this.document?.requiredDecisions ?? null,
        }
      : this.stepDecisionRequirements();
  }

  private setFormDirty(value: boolean): void {
    if (this.formDirty() === value) {
      return;
    }
    this.pendingFormBaseline = value ? this.pendingFormSource(this.document) : null;
    this.formDirty.set(value);
    this.emitPendingChanges();
  }

  private emitPendingChanges(): void {
    const pending =
      this.businessStatusDirty() ||
      this.progressSettingsDirty() ||
      this.formDirty() ||
      this.notificationFormDirty ||
      !!this.lifecycleImpact();
    if (pending === this.lastPendingState) return;
    this.lastPendingState = pending;
    this.pendingChangesChange.emit(pending);
  }

  onBusinessStatusPending(value: boolean): void {
    this.businessStatusDirty.set(value);
    this.emitPendingChanges();
  }
  onBusinessStatusDocument(value: WorkflowDefinitionDocument): void {
    if (this.stepStatusesWorkspace) return;
    this.document = value;
    this.documentChange.emit(value);
  }
  onProgressSettingsPending(value: boolean): void {
    this.progressSettingsDirty.set(value);
    this.emitPendingChanges();
  }
  requestWorkflowStatuses(): void {
    if (this.externalWorkflowStatuses) { this.requestExternalWorkflowStatuses(); return; }
    const step = this.activeHumanStep;
    if (
      !step ||
      this.interactionBlocked ||
      this.notificationEditorPending ||
      this.lifecycleImpact()
    )
      return;
    this.workflowStatusesRequested.emit(step.id);
  }

  /** The parent owns workflow-wide labels; this editor retains step labels and lifecycle checks. */
  private requestExternalWorkflowStatuses(path?: string, diagnosticSource?: 'validation' | 'review'): void {
    const owner = JSON.stringify(this.ownerContext?.()) ?? '', view = this.workflowStatusesView?.();
    const nodeId = this.activeHumanStep?.id ?? null, scope = JSON.stringify(this.selectedScope());
    const localContext = this.stepStatusesWorkspace && nodeId !== null ? this.workflowStatusesRequestContext() : null;
    const currentDocument = () => this.latestDocument ? this.latestDocument() : this.document;
    const available = () => this.externalWorkflowStatuses && !!this.ownerContext && !!view && view === this.externalStatusNavigationView
      && view === this.workflowStatusesView?.()
      && owner === this.externalStatusNavigationOwner && owner === JSON.stringify(this.ownerContext?.())
      && !this.destroyRef.destroyed && !this.interactionBlocked && !this.ownerViews.focusSuspended()
      && (!this.stepStatusesWorkspace || nodeId === null || this.stepStatusesView() === 'overview' && localContext === this.workflowStatusesRequestContext() && this.stepStatusesParentCurrent())
      && !this.notificationEditorPending && !this.lifecycleImpact() && scope === JSON.stringify(this.selectedScope());
    const currentOrigin = (document: WorkflowDefinitionDocument | null): boolean => !!document && !!this.document
      && JSON.stringify([document.workflowCode, document.definitionVersionCode, document.packReference])
        === JSON.stringify([this.document.workflowCode, this.document.definitionVersionCode, this.document.packReference])
      && (nodeId === null || document.nodes.filter(node => node?.id === nodeId && node.typeCode === 'human_step').length === 1);
    if (!available() || !currentOrigin(currentDocument())) return;
    this.requestDiscardPending(() => {
      const document = currentDocument();
      if (!available() || !currentOrigin(document)) return;
      this.workflowStatusesOwnerRequested.emit({ nodeId, origin: nodeId === null ? 'workflow-checks' : 'step-statuses', owner,
        source: JSON.stringify(document), view: view!, ...(path === undefined ? {} : { path }),
        ...(localContext === null ? {} : { localContext }),
        ...(diagnosticSource === undefined ? {} : { diagnosticSource }) });
    }, () => undefined, currentDocument());
  }

  private ensureFormValid(): boolean {
    const formGroup = this.transitionForm?.theFormGroup;
    if (formGroup?.valid || (formGroup?.disabled && this.pendingFormState(this.document) === 'valid')) {
      return true;
    }
    formGroup?.markAllAsTouched();
    this.showLocalError(
      'Complete the required status fields before applying changes.',
    );
    return false;
  }

  private showLocalError(message: string): void {
    this.localError.set(message);
    this.focusAfterRender(() => this.transitionError);
  }

  private nodeLabel(node: WorkflowDefinitionNode): string {
    return node.displayName || node.stepCode || node.id;
  }

  private nodeTypeLabel(
    node: WorkflowDefinitionNode | null | undefined,
  ): string {
    switch (node?.typeCode) {
      case 'entry':
        return 'Start boundary';
      case 'terminal':
        return 'Completion boundary';
      case 'human_step':
        return 'Work step';
      default:
        return 'Non-work-step node';
    }
  }

  private emptyFormValues(): TransitionFormValues {
    return {
      scopeCode: 'workflow',
      stepNodeId: '',
      fromStateCode: '',
      toStateCode: '',
    };
  }

  private confirmLocally(request: Parameters<WorkflowDraftConfirmationService['confirm']>[0]): Promise<boolean> {
    if (this.destroyRef.destroyed || this.localConfirmationInProgress || this.ownerViews.focusSuspended()) return Promise.resolve(false);
    this.localConfirmationInProgress = true;
    const resumeFocus = this.ownerViews.suspendFocus();
    const host = this.host.nativeElement, wasInert = host.inert;
    host.inert = true;
    const release = () => {
      this.localConfirmationInProgress = false;
      host.inert = wasInert;
      resumeFocus();
    };
    try {
      const result = this.confirmation.confirm(request);
      void result.then(release, release);
      return result;
    } catch (error) { release(); throw error; }
  }

  private focusAfterRender(
    target: () => ElementRef<HTMLElement> | undefined,
  ): void {
    setTimeout(() => {
      const element = target()?.nativeElement;
      if (this.destroyRef.destroyed || this.ownerViews.focusSuspended() || element?.closest('[hidden],[inert]')) return;
      element?.focus();
    });
  }
}

interface PersistedTransitionSelector {
  index: number | null;
  fromStateCode: string | null;
  toStateCode: string | null;
}

function persistedTransitionSelector(
  path: string,
): PersistedTransitionSelector | null {
  const indexedMatch =
    /(?:workflowTransitions|allowedTransitions)\[(\d+)\]/.exec(path);
  if (indexedMatch) {
    return {
      index: Number.parseInt(indexedMatch[1], 10),
      fromStateCode: null,
      toStateCode: null,
    };
  }

  const transitionMatch = /\[transition=([^\]]+)\]/.exec(path);
  const transitionId = transitionMatch?.[1] ?? '';
  const separatorIndex = transitionId.indexOf('->');
  if (separatorIndex < 0) {
    return null;
  }

  return {
    index: null,
    fromStateCode: transitionId.slice(0, separatorIndex),
    toStateCode: transitionId.slice(separatorIndex + 2),
  };
}

function stepNodeIdFromPersistedPath(path: string): string | null {
  return /^\$\.nodes\[id=([^\]]+)\]/.exec(path)?.[1] ?? null;
}
